import React, { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import JSON5 from "json5";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import CheckTick from "../../assets/icons/Check-Tick.png";
import "../forms/formTitle.css";
import "../forms/themeCheckbox.css";
import "../forms/themePlaceholder.css";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  ADMIN_THEME,
  adminEditLabelStyle,
  adminEditInputStyle,
  parseSiteRegisterDate,
  formatSiteRegisterDate,
  formatIsoDate,
  siteRegisterDatePickerProps,
  adminEditModalOverlayStyle,
  adminEditModalContentStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditSigBoxStyle,
} from "./adminEditModalTheme";

function PreFabricatedPage() {
  const canEditDelete = canEditDeleteRecords();
  const [records, setRecords] = useState([]);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [showViewModal, setShowViewModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [newRecord, setNewRecord] = useState({
    formUuid: "",
    userId: "",
    inspector: "",
    employerNo: "",
    pasmaCardNo: "",
    userName1: "",
    employerNo1: "",
    pasmaCardNo1: "",
    userName2: "",
    employerNo2: "",
    pasmaCardNo2: "",
    inspectionDate: "",
    towerNo: "",
    platformHeight: "",
    projectJobNo: "",
    reasonForTower: "",
    mcqQuestionsList: []
  });
  const [editSignature, setEditSignature] = useState("");
  const sigCanvasRef = useRef(null);
  const editSigCanvasRef = useRef(null);
  const itemSignatureRefs = useRef({});
  const editItemSignatureRefs = useRef({});
  const savingRef = useRef(false); // Prevent double submission
  const [isSaving, setIsSaving] = useState(false); // UI state for saving
  const [uploadingKey, setUploadingKey] = useState(null);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  const getMcqPrintNameValue = (item) => {
    if (!item) return "";
    const tf = item.textField || {};
    return String(item.textFieldValue ?? tf.valueType ?? tf.value ?? "").trim();
  };

  // Normalize MCQ data coming from API (strings, booleans, etc.) - Updated to match PowAssessmentPage structure
  const normalizeMcq = (sections) => {
    if (!Array.isArray(sections)) return [];
    return sections.map(section => {
      // Handle both 'item' (singular) and 'items' (plural) from API
      const rawItems = section.item || section.items || [];
      const items = Array.isArray(rawItems) ? rawItems.map(item => {
        const raw = item.value;
        let value = raw;
        if (Array.isArray(item.options) && item.options.length && typeof raw === 'string') {
          const lower = raw.toLowerCase();
          if (lower === 'true') value = 'Yes';
          else if (lower === 'false') value = 'No';
        }
        const textField = item.textField || {};
        const printDate = item.printDate || {};
        const signature = item.signature || {};
        const printNameVal = item.textFieldValue || textField.valueType || textField.value || '';
        return {
          ...item,
          value,
          textFieldValue: printNameVal,
          textField: {
            isTextBox: !!textField.isTextBox,
            hintText: textField.hintText || '',
            valueType: textField.isTextBox ? (textField.valueType || textField.value || '') : '',
            value: textField.value || textField.valueType || '',
            na: !!textField.na
          },
          printDate: {
            isPrintDate: !!printDate.isPrintDate,
            printValue: printDate.printValue || ''
          },
          signature: {
            isSigned: !!signature.isSigned,
            signValue: signature.signValue || ''
          },
          checkboxValue: !!item.checkboxValue,
          hasCheckbox: !!item.hasCheckbox
        };
      }) : [];
      return {
        ...section,
        items: items,
        checkboxOptions: section.checkboxOptions || [],
        checkboxValues: section.checkboxValues || {}
      };
    });
  };

  // Signature pad handlers for Add Modal
  const clearSignature = () => {
    try {
      if (sigCanvasRef.current) {
        sigCanvasRef.current.clear();
      } else {
      }
      setNewRecord(prev => ({ ...prev, signature: "" }));
    } catch (error) {
      console.error('Error clearing signature:', error);
    }
  };
  const saveSignature = () => {
    if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = sigCanvasRef.current.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setNewRecord(prev => ({
        ...prev,
        signature: sig
      }));
    }
  };

  // Signature pad handlers for Edit Modal
  const clearEditSignature = () => {
    try {
      if (editSigCanvasRef.current) {
        editSigCanvasRef.current.clear();
      } else {
      }
      setEditSignature("");
      setEditRecord(prev => ({ ...prev, signature: "" }));
    } catch (error) {
      console.error('Error clearing edit signature:', error);
    }
  };
  const saveEditSignature = () => {
    if (editSigCanvasRef.current && !editSigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = editSigCanvasRef.current.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setEditSignature(sig);
      setEditRecord(prev => ({ ...prev, signature: sig }));
    }
  };

  // Item signature handlers for Add Modal - Updated to match PowAssessmentPage structure
  const clearItemSignature = (sectionIdx, itemIdx) => {
    try {
      const key = `${sectionIdx}-${itemIdx}`;
      const canvasRef = itemSignatureRefs.current[key];
      if (canvasRef && canvasRef.clear) {
        canvasRef.clear();
        // Restore the white background after clearing
        const canvas = canvasRef.getCanvas();
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else {
      }
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = '';
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    } catch (error) {
      console.error('Error clearing item signature:', error);
    }
  };

  const saveItemSignature = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    const canvasRef = itemSignatureRefs.current[key];
    if (canvasRef && !canvasRef.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = canvasRef.getCanvas();
      const sig = canvas.toDataURL("image/png");
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = sig;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  // Item signature handlers for Edit Modal - Updated to match PowAssessmentPage structure
  const clearEditItemSignature = (sectionIdx, itemIdx) => {
    try {
      if (!editRecord || !editRecord.mcqQuestionsList) {
        return;
      }
      const key = `${sectionIdx}-${itemIdx}`;
      const canvasRef = editItemSignatureRefs.current[key];
      if (canvasRef && canvasRef.clear) {
        canvasRef.clear();
        // Restore the white background after clearing
        const canvas = canvasRef.getCanvas();
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      } else {
      }
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = '';
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } catch (error) {
      console.error('Error clearing edit item signature:', error);
    }
  };

  const saveEditItemSignature = (sectionIdx, itemIdx) => {
    if (!editRecord || !editRecord.mcqQuestionsList) return;
    const key = `${sectionIdx}-${itemIdx}`;
    const canvasRef = editItemSignatureRefs.current[key];
    if (canvasRef && !canvasRef.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = canvasRef.getCanvas();
      const sig = canvas.toDataURL("image/png");
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].signature.signValue = sig;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
    }
  };

  // Load existing signatures when edit modal opens - Updated to match PowAssessmentPage structure
  useEffect(() => {
    if (showEditModal && editRecord && editRecord.mcqQuestionsList) {
      const timer = setTimeout(() => {
        editRecord.mcqQuestionsList.forEach((section, sectionIdx) => {
          if (Array.isArray(section.items)) {
            section.items.forEach((item, itemIdx) => {
              if (item.signature?.isSigned && item.signature?.signValue) {
                const key = `${sectionIdx}-${itemIdx}`;
                const canvasRef = editItemSignatureRefs.current[key];
                if (canvasRef && canvasRef.current) {
                  canvasRef.current.clear();
                  let sigSrc = item.signature.signValue;
                  if (!sigSrc.startsWith('data:image') && /^[A-Za-z0-9+/=]+$/.test(sigSrc)) {
                    sigSrc = 'data:image/png;base64,' + sigSrc;
                  }
                  if (sigSrc.startsWith('data:image')) {
                    const img = new window.Image();
                    img.src = sigSrc;
                    img.onload = () => {
                      const ctx = canvasRef.current.getCanvas().getContext('2d');
                      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                      ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
                    };
                  }
                }
              }
            });
          }
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showEditModal, editRecord]);

  useEffect(() => {
    async function fetchRecords() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/pre-fabricated", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setRecords(data.data);
        } else {
          setRecords([]);
        }
      } catch {
        setRecords([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchRecords();
    fetchAppUsers();
  }, []);

  // Fetch MCQ template when opening Add Modal
  const fetchMcqTemplate = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + "/api/pre-fabricated/template", {
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setNewRecord(prev => ({ ...prev, mcqQuestionsList: data.data }));
      }
    } catch (error) {
    }
  };

  // Search logic
  const filtered = records.filter(r => {
    const fields = [r.inspector, r.towerNo, r.projectJobNo, r.reasonForTower].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);

  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  // Add Record
  const handleAdd = async () => {
    if (!newRecord.inspector || !newRecord.employerNo || !newRecord.pasmaCardNo || !newRecord.inspectionDate || !newRecord.towerNo || !newRecord.platformHeight || !newRecord.projectJobNo || !newRecord.reasonForTower) {
      toast.error("All required fields must be filled");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const mcqForSave = (newRecord.mcqQuestionsList || []).map(sec => ({
        ...sec,
        items: (sec.items || []).map(it => ({
          ...it,
          value: it.value || '',
          textField: {
            ...it.textField,
            valueType: it.textField?.isTextBox ? (it.textFieldValue || it.textField?.valueType || '') : ''
          }
        }))
      }));
      
      // Convert to string format similar to the original data structure
      const mcqString = mcqForSave.map(sec => {
        const header = sec.header || '';
        const items = sec.items.map(it => {
          const text = it.text || '';
          const options = it.options || [];
          const value = it.value || '';
          const textField = it.textField || {};
          const printDate = it.printDate || {};
          const signature = it.signature || {};
          const isOptional = it.isOptional || false;
          
          // Escape single quotes in text fields to prevent string breaking
          const safeText = text.replace(/'/g, "\\'");
          const safeHintText = (textField.hintText || '').replace(/'/g, "\\'");
          const safeValueType = textField.isTextBox ? (textField.valueType || '').replace(/'/g, "\\'") : '';
          const safePrintValue = (printDate.printValue || '').replace(/'/g, "\\'");
          const safeSignValue = (signature.signValue || '').replace(/'/g, "\\'");
          
          return `{text: '${safeText}', options: [${options.map(opt => `'${opt}'`).join(',')}], value: '${value}', textField: {isTextBox: ${textField.isTextBox || false}, hintText: '${safeHintText}', valueType: '${safeValueType}'}, printDate: {isPrintDate: ${printDate.isPrintDate || false}, printValue: '${safePrintValue}'}, signature: {isSigned: ${signature.isSigned || false}, signValue: '${safeSignValue}'}, isOptional: ${isOptional}}`;
        }).join(', ');
        return `{header: '${header.replace(/'/g, "\\'")}', item: [${items}]}`;
      }).join(', ');
      
      // Get user details for saving firstName and lastName
      const user = appUsers.find(u => u._id === newRecord.userId);
      const addedByFirstName = user ? user.firstName : '';
      const addedByLastName = user ? user.lastName : '';
      
      const payload = { 
        ...newRecord, 
        formUuid: ensureFormUuid(newRecord.formUuid),
        addedByFirstName,
        addedByLastName,
        mcqQuestionsList: `[${mcqString}]` 
      };
      const res = await fetch(baseUrl + "/api/pre-fabricated", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        setRecords([...records, data.data]);
        setShowAddModal(false);
        setNewRecord(prev => ({
          ...prev,
          formUuid: "",
          userId: "",
          inspector: "",
          employerNo: "",
          pasmaCardNo: "",
          userName1: "",
          employerNo1: "",
          pasmaCardNo1: "",
          userName2: "",
          employerNo2: "",
          pasmaCardNo2: "",
          inspectionDate: "",
          towerNo: "",
          platformHeight: "",
          projectJobNo: "",
          reasonForTower: "",
          mcqQuestionsList: [],
          signature: ""
        }));
        toast.success("Record added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  // Function to convert date for saving
  const convertDateForSave = (date) => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    return date;
  };

  // Function to update only value fields while preserving complete API structure
  const updateOnlyValueFields = (originalData, formData) => {
    try {
      // Deep clone the original data to avoid mutations
      const updatedData = JSON.parse(JSON.stringify(originalData));
      
      // Update basic form fields
      const basicFields = [
        'inspector', 'employerNo', 'pasmaCardNo', 'userName1', 'employerNo1', 'pasmaCardNo1',
        'userName2', 'employerNo2', 'pasmaCardNo2', 'inspectionDate', 'towerNo', 
        'platformHeight', 'projectJobNo', 'reasonForTower', 'userId', 'signature'
      ];
      
      basicFields.forEach(field => {
        if (formData[field] !== undefined) {
          updatedData[field] = formData[field];
        }
      });
      
      // Debug: Check what's actually in formData
      
      // Get form data from mcqQuestionsList - use the actual form data structure
      const formMcqData = formData.mcqQuestionsList || [];
      
      // The form data structure has sections with headers like 'Before And During Construction'
      // We need to get all items from all sections
      const mcqQuestionsListItems = [];
      formMcqData.forEach((section, index) => {
        if (section.items && Array.isArray(section.items)) {
          mcqQuestionsListItems.push(...section.items);
        } else {
        }
      });
      
      
      // Create a simple map of form data items by title for easier lookup
      const formDataMap = {};
      
      mcqQuestionsListItems.forEach((item, index) => {
        if (item.text) {
          formDataMap[item.text] = {
            title: item.text,
            value: item.value,
            textFieldValue: item.textFieldValue || '',
            valueType: item.textField?.valueType || '',
            printValue: item.printDate?.printValue || '',
            signValue: item.signature?.signValue || '',
            checkboxValue: item.checkboxValue || false,
            hasCheckbox: item.hasCheckbox || false
          };
        }
      });
      
      
      // Update the mcqQuestionsList string directly using string manipulation
      let updatedMcqQuestionsList = originalData.mcqQuestionsList;
      
      // Debug: Log original data
      
      
      // Process each item and update values
      Object.keys(formDataMap).forEach(itemTitle => {
        const item = formDataMap[itemTitle];
        
          // Update radio button values - Use a more robust approach
          if (item.value !== undefined) {
            
            // Try to find the text field - prioritize double quotes (JSON format) then single quotes
            // First try with double quotes (standard JSON)
            let titleIndex = updatedMcqQuestionsList.indexOf(`"text":"${item.title}"`);
            if (titleIndex === -1) {
              titleIndex = updatedMcqQuestionsList.indexOf(`"text": "${item.title}"`);
            }
            // Fallback to single quotes
            if (titleIndex === -1) {
              titleIndex = updatedMcqQuestionsList.indexOf(`'text':'${item.title}'`);
            }
            if (titleIndex === -1) {
              titleIndex = updatedMcqQuestionsList.indexOf(`'text': '${item.title}'`);
            }
            
            if (titleIndex !== -1) {
              
              // Find the value field after this title - look for both single and double quotes
              const valuePatterns = [`"value":"`, `"value": "`, `'value':'`, `'value': '`];
              let valueStartIndex = -1;
              let valuePattern = '';
              
              // Search from titleIndex onwards
              const searchStart = titleIndex;
              for (const pattern of valuePatterns) {
                const index = updatedMcqQuestionsList.indexOf(pattern, searchStart);
                if (index !== -1 && index < searchStart + 5000) { // Limit search range to reasonable distance
                  valueStartIndex = index;
                  valuePattern = pattern;
                  break;
                }
              }
              
              if (valueStartIndex !== -1) {
                const valueStart = valueStartIndex + valuePattern.length;
                // Find the end of the value - look for the next quote (handling escaped quotes)
                const quoteChar = valuePattern.includes("'") ? "'" : '"';
                let valueEndIndex = valueStart;
                let foundEnd = false;
                
                // Look for the closing quote, handling escaped quotes
                while (valueEndIndex < updatedMcqQuestionsList.length && !foundEnd) {
                  const nextQuoteIndex = updatedMcqQuestionsList.indexOf(quoteChar, valueEndIndex);
                  if (nextQuoteIndex === -1) break;
                  
                  // Check if the quote is escaped (preceded by backslash)
                  if (nextQuoteIndex > 0 && updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\') {
                    // Check if it's not part of an escape sequence (double backslash)
                    if (nextQuoteIndex === 0 || updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\' || 
                        (nextQuoteIndex > 1 && updatedMcqQuestionsList[nextQuoteIndex - 2] === '\\')) {
                      valueEndIndex = nextQuoteIndex;
                      foundEnd = true;
                      break;
                    }
                  }
                  valueEndIndex = nextQuoteIndex + 1;
                }
                
                if (foundEnd) {
                  const currentValue = updatedMcqQuestionsList.substring(valueStart, valueEndIndex);
                  
                  // Escape the new value if it contains quotes
                  let newValue = item.value;
                  if (newValue.includes(quoteChar)) {
                    newValue = newValue.replace(new RegExp(`\\\\${quoteChar}`, 'g'), quoteChar)
                                       .replace(new RegExp(quoteChar, 'g'), `\\${quoteChar}`);
                  }
                  
                  // Replace the value
                  updatedMcqQuestionsList = updatedMcqQuestionsList.substring(0, valueStart) + 
                                           newValue + 
                                           updatedMcqQuestionsList.substring(valueEndIndex);
                  
                } else {
                }
              } else {
              }
            } else {
            }
          }
        
        // Update checkbox values
        if (item.checkboxValue !== undefined) {
          const escapedTitle = item.title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const checkboxPattern = new RegExp(
            `(\\{'text':'${escapedTitle}'[^}]*'hasCheckbox':)(true|false)([^}]*'checkboxValue':)(true|false)([^}]*\\})`,
            'g'
          );
          
          updatedMcqQuestionsList = updatedMcqQuestionsList.replace(checkboxPattern, (match, prefix1, currentHasCheckbox, prefix2, currentCheckboxValue, suffix) => {
            return `${prefix1}${item.hasCheckbox || false}${prefix2}${item.checkboxValue}${suffix}`;
          });
        }
        
        // Update text field values (valueType) - Use robust approach
        // Only update valueType if isTextBox is true and we have a textFieldValue
        if (item.textFieldValue !== undefined && item.textFieldValue !== '') {
          
          // Try to find the text field - prioritize double quotes (JSON format) then single quotes
          // First try with double quotes (standard JSON)
          let titleIndex = updatedMcqQuestionsList.indexOf(`"text":"${item.title}"`);
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`"text": "${item.title}"`);
          }
          // Fallback to single quotes
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`'text':'${item.title}'`);
          }
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`'text': '${item.title}'`);
          }
          
          if (titleIndex !== -1) {
            // Look for both single and double quotes
            const valueTypePatterns = [`"valueType":"`, `"valueType": "`, `'valueType':'`, `'valueType': '`];
            let valueTypeStartIndex = -1;
            let valueTypePattern = '';
            
            for (const pattern of valueTypePatterns) {
              const index = updatedMcqQuestionsList.indexOf(pattern, titleIndex);
              if (index !== -1 && index < titleIndex + 5000) {
                valueTypeStartIndex = index;
                valueTypePattern = pattern;
                break;
              }
            }
            
            if (valueTypeStartIndex !== -1) {
              const valueTypeStart = valueTypeStartIndex + valueTypePattern.length;
              const quoteChar = valueTypePattern.includes("'") ? "'" : '"';
              let valueTypeEndIndex = valueTypeStart;
              let foundEnd = false;
              
              // Look for the closing quote, handling escaped quotes
              while (valueTypeEndIndex < updatedMcqQuestionsList.length && !foundEnd) {
                const nextQuoteIndex = updatedMcqQuestionsList.indexOf(quoteChar, valueTypeEndIndex);
                if (nextQuoteIndex === -1) break;
                
                if (nextQuoteIndex > 0 && updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\') {
                  if (nextQuoteIndex === 0 || updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\' || 
                      (nextQuoteIndex > 1 && updatedMcqQuestionsList[nextQuoteIndex - 2] === '\\')) {
                    valueTypeEndIndex = nextQuoteIndex;
                    foundEnd = true;
                    break;
                  }
                }
                valueTypeEndIndex = nextQuoteIndex + 1;
              }
              
              if (foundEnd) {
                const currentValue = updatedMcqQuestionsList.substring(valueTypeStart, valueTypeEndIndex);
                
                // Escape the new value if it contains quotes
                let newValue = item.textFieldValue;
                if (newValue.includes(quoteChar)) {
                  newValue = newValue.replace(new RegExp(`\\\\${quoteChar}`, 'g'), quoteChar)
                                     .replace(new RegExp(quoteChar, 'g'), `\\${quoteChar}`);
                }
                
                updatedMcqQuestionsList = updatedMcqQuestionsList.substring(0, valueTypeStart) + 
                                        newValue + 
                                        updatedMcqQuestionsList.substring(valueTypeEndIndex);
                
              }
            }
          }
        }
        
        // Update print date values - Use robust approach
        if (item.printValue !== undefined && item.printValue !== '') {
          
          // Try to find the text field - prioritize double quotes (JSON format) then single quotes
          // First try with double quotes (standard JSON)
          let titleIndex = updatedMcqQuestionsList.indexOf(`"text":"${item.title}"`);
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`"text": "${item.title}"`);
          }
          // Fallback to single quotes
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`'text':'${item.title}'`);
          }
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`'text': '${item.title}'`);
          }
          
          if (titleIndex !== -1) {
            // Look for both single and double quotes
            const printValuePatterns = [`"printValue":"`, `"printValue": "`, `'printValue':'`, `'printValue': '`];
            let printValueStartIndex = -1;
            let printValuePattern = '';
            
            for (const pattern of printValuePatterns) {
              const index = updatedMcqQuestionsList.indexOf(pattern, titleIndex);
              if (index !== -1 && index < titleIndex + 5000) {
                printValueStartIndex = index;
                printValuePattern = pattern;
                break;
              }
            }
            
            if (printValueStartIndex !== -1) {
              const printValueStart = printValueStartIndex + printValuePattern.length;
              const quoteChar = printValuePattern.includes("'") ? "'" : '"';
              let printValueEndIndex = printValueStart;
              let foundEnd = false;
              
              // Look for the closing quote, handling escaped quotes
              while (printValueEndIndex < updatedMcqQuestionsList.length && !foundEnd) {
                const nextQuoteIndex = updatedMcqQuestionsList.indexOf(quoteChar, printValueEndIndex);
                if (nextQuoteIndex === -1) break;
                
                if (nextQuoteIndex > 0 && updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\') {
                  if (nextQuoteIndex === 0 || updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\' || 
                      (nextQuoteIndex > 1 && updatedMcqQuestionsList[nextQuoteIndex - 2] === '\\')) {
                    printValueEndIndex = nextQuoteIndex;
                    foundEnd = true;
                    break;
                  }
                }
                printValueEndIndex = nextQuoteIndex + 1;
              }
              
              if (foundEnd) {
                const currentValue = updatedMcqQuestionsList.substring(printValueStart, printValueEndIndex);
                
                // Escape the new value if it contains quotes
                let newValue = item.printValue;
                if (newValue.includes(quoteChar)) {
                  newValue = newValue.replace(new RegExp(`\\\\${quoteChar}`, 'g'), quoteChar)
                                     .replace(new RegExp(quoteChar, 'g'), `\\${quoteChar}`);
                }
                
                updatedMcqQuestionsList = updatedMcqQuestionsList.substring(0, printValueStart) + 
                                        newValue + 
                                        updatedMcqQuestionsList.substring(printValueEndIndex);
                
              }
            }
          }
        }
        
        // Update signature values - Use robust approach
        if (item.signValue !== undefined && item.signValue !== '') {
          
          // Try to find the text field - prioritize double quotes (JSON format) then single quotes
          // First try with double quotes (standard JSON)
          let titleIndex = updatedMcqQuestionsList.indexOf(`"text":"${item.title}"`);
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`"text": "${item.title}"`);
          }
          // Fallback to single quotes
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`'text':'${item.title}'`);
          }
          if (titleIndex === -1) {
            titleIndex = updatedMcqQuestionsList.indexOf(`'text': '${item.title}'`);
          }
          
          if (titleIndex !== -1) {
            // Look for both single and double quotes
            const signValuePatterns = [`"signValue":"`, `"signValue": "`, `'signValue':'`, `'signValue': '`];
            let signValueStartIndex = -1;
            let signValuePattern = '';
            
            for (const pattern of signValuePatterns) {
              const index = updatedMcqQuestionsList.indexOf(pattern, titleIndex);
              if (index !== -1 && index < titleIndex + 5000) {
                signValueStartIndex = index;
                signValuePattern = pattern;
                break;
              }
            }
            
            if (signValueStartIndex !== -1) {
              const signValueStart = signValueStartIndex + signValuePattern.length;
              const quoteChar = signValuePattern.includes("'") ? "'" : '"';
              let signValueEndIndex = signValueStart;
              let foundEnd = false;
              
              // Look for the closing quote, handling escaped quotes
              while (signValueEndIndex < updatedMcqQuestionsList.length && !foundEnd) {
                const nextQuoteIndex = updatedMcqQuestionsList.indexOf(quoteChar, signValueEndIndex);
                if (nextQuoteIndex === -1) break;
                
                if (nextQuoteIndex > 0 && updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\') {
                  if (nextQuoteIndex === 0 || updatedMcqQuestionsList[nextQuoteIndex - 1] !== '\\' || 
                      (nextQuoteIndex > 1 && updatedMcqQuestionsList[nextQuoteIndex - 2] === '\\')) {
                    signValueEndIndex = nextQuoteIndex;
                    foundEnd = true;
                    break;
                  }
                }
                signValueEndIndex = nextQuoteIndex + 1;
              }
              
              if (foundEnd) {
                const currentValue = updatedMcqQuestionsList.substring(signValueStart, signValueEndIndex);
                
                // Escape the new value if it contains quotes (signatures are base64, usually don't have quotes)
                let newValue = item.signValue;
                if (newValue.includes(quoteChar)) {
                  newValue = newValue.replace(new RegExp(`\\\\${quoteChar}`, 'g'), quoteChar)
                                     .replace(new RegExp(quoteChar, 'g'), `\\${quoteChar}`);
                }
                
                updatedMcqQuestionsList = updatedMcqQuestionsList.substring(0, signValueStart) + 
                                        newValue + 
                                        updatedMcqQuestionsList.substring(signValueEndIndex);
                
              }
            }
          }
        }
      });
      
      // Update the mcqQuestionsList in the result
      updatedData.mcqQuestionsList = updatedMcqQuestionsList;
      
      // Debug: Log the final result to verify updates are working
      
      // Update other form fields
      updatedData.locationDescription = formData.locationDescription;
      updatedData.date = formData.date;
      updatedData.jobNumber = formData.jobNumber;
      updatedData.personInCharge = formData.personInCharge;
      updatedData.name = formData.name;
      updatedData.signature = formData.signature;
      
      return updatedData;
    } catch (error) {
      console.error('Error updating value fields:', error);
      return originalData;
    }
  };

  // Function to fix unescaped quotes and malformed JSON structures
  const fixUnescapedQuotes = (jsonStr) => {
    try {
      let fixed = jsonStr;
      
      // Fix quotes within text values
      fixed = fixed.replace(/("text"\s*:\s*")([^"]*"[^"]*"[^"]*)(")/g, (match, prefix, content, suffix) => {
        const escapedContent = content.replace(/"/g, '\\"');
        return prefix + escapedContent + suffix;
      });
      
      // Fix quotes within hintText values
      fixed = fixed.replace(/("hintText"\s*:\s*")([^"]*"[^"]*"[^"]*)(")/g, (match, prefix, content, suffix) => {
        const escapedContent = content.replace(/"/g, '\\"');
        return prefix + escapedContent + suffix;
      });
      
      // Fix quotes within valueType values
      fixed = fixed.replace(/("valueType"\s*:\s*")([^"]*"[^"]*"[^"]*)(")/g, (match, prefix, content, suffix) => {
        const escapedContent = content.replace(/"/g, '\\"');
        return prefix + escapedContent + suffix;
      });
      
      // Fix quotes within printValue values
      fixed = fixed.replace(/("printValue"\s*:\s*")([^"]*"[^"]*"[^"]*)(")/g, (match, prefix, content, suffix) => {
        const escapedContent = content.replace(/"/g, '\\"');
        return prefix + escapedContent + suffix;
      });
      
      // Fix quotes within signValue values
      fixed = fixed.replace(/("signValue"\s*:\s*")([^"]*"[^"]*"[^"]*)(")/g, (match, prefix, content, suffix) => {
        const escapedContent = content.replace(/"/g, '\\"');
        return prefix + escapedContent + suffix;
      });
      
      // Fix malformed object structures - this is the main issue
      // The problem is that the JSON structure is fundamentally broken
      // We need to reconstruct the proper structure
      
      // Fix the specific pattern we're seeing: "hintText":",\"valueType\":"
      fixed = fixed.replace(/"hintText"\s*:\s*",\\"valueType"\s*:\s*"/g, '"hintText":"","valueType":"');
      
      // Fix the specific pattern: "printValue":"},\"signature\":"
      fixed = fixed.replace(/"printValue"\s*:\s*"},\\"signature"\s*:\s*"/g, '"printValue":"","signature":{"isSigned":false,"signValue":""');
      
      // Fix cases where objects are not properly closed
      fixed = fixed.replace(/"hintText"\s*:\s*"([^"]*)"\s*,\s*"valueType"\s*:\s*"([^"]*)"\s*}/g, '"hintText":"$1","valueType":"$2"}');
      
      // Fix cases where signature object is malformed
      fixed = fixed.replace(/"signValue"\s*:\s*"([^"]*)"\s*}\s*,\s*"isOptional"/g, '"signValue":"$1"},"isOptional"');
      
      // Fix cases where printDate object is malformed
      fixed = fixed.replace(/"printValue"\s*:\s*"([^"]*)"\s*}\s*,\s*"signature"/g, '"printValue":"$1"},"signature"');
      
      // Fix cases where textField object is malformed
      fixed = fixed.replace(/"valueType"\s*:\s*"([^"]*)"\s*}\s*,\s*"printDate"/g, '"valueType":"$1"},"printDate"');
      
      // Additional fixes for the specific malformed structure we're seeing
      // Fix cases where the structure is completely broken
      fixed = fixed.replace(/"textField"\s*:\s*\{\s*"isTextBox"\s*:\s*false\s*,\s*"hintText"\s*:\s*"([^"]*)"\s*,\s*"valueType"\s*:\s*"([^"]*)"\s*\}\s*,\s*"printDate"\s*:\s*\{\s*"isPrintDate"\s*:\s*false\s*,\s*"printValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"signature"\s*:\s*\{\s*"isSigned"\s*:\s*false\s*,\s*"signValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"isOptional"\s*:\s*false/g, 
        '"textField":{"isTextBox":false,"hintText":"$1","valueType":"$2"},"printDate":{"isPrintDate":false,"printValue":"$3"},"signature":{"isSigned":false,"signValue":"$4"},"isOptional":false');
      
      // Fix cases where the structure is partially broken
      fixed = fixed.replace(/"hintText"\s*:\s*"([^"]*)"\s*,\s*"valueType"\s*:\s*"([^"]*)"\s*\}\s*,\s*"printDate"/g, '"hintText":"$1","valueType":"$2"},"printDate"');
      
      // Fix cases where printDate structure is broken
      fixed = fixed.replace(/"printValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"signature"/g, '"printValue":"$1"},"signature"');
      
      // Fix cases where signature structure is broken
      fixed = fixed.replace(/"signValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"isOptional"/g, '"signValue":"$1"},"isOptional"');
      
      return fixed;
    } catch (error) {
      return jsonStr;
    }
  };

  // Custom MCQ parser for malformed strings - Updated to handle new structure
  const parseCustomMcqFormat = (str) => {
    try {
      // Remove the outer brackets and split by sections
      const cleanStr = str.replace(/^\[|\]$/g, '');
      
      // Split by section headers (looking for both {'header': and {header: patterns)
      const sections = [];
      let currentSection = '';
      let braceCount = 0;
      let inSection = false;
      
      for (let i = 0; i < cleanStr.length; i++) {
        const char = cleanStr[i];
        if (char === '{') {
          braceCount++;
          // Check for both patterns: {'header': and {header:
          if (cleanStr.substring(i, i + 10) === "{'header':" || 
              cleanStr.substring(i, i + 8) === '{header:') {
            if (currentSection.trim()) {
              sections.push(currentSection.trim());
            }
            currentSection = '';
            inSection = true;
          }
        }
        
        if (inSection) {
          currentSection += char;
        }
        
        if (char === '}') {
          braceCount--;
          if (braceCount === 0 && inSection) {
            sections.push(currentSection.trim());
            currentSection = '';
            inSection = false;
          }
        }
      }
      
      
      // Parse each section
      const parsedSections = sections.map(section => {
        try {
          // Extract header (handle both 'header': and header: patterns)
          const headerMatch = section.match(/(?:'header'|header):\s*([^,]+)/);
          const header = headerMatch ? headerMatch[1].trim().replace(/['"]/g, '') : '';
          
          // Extract checkboxOptions
          const checkboxOptionsMatch = section.match(/checkboxOptions:\s*\[([^\]]*)\]/);
          const checkboxOptions = checkboxOptionsMatch ? 
            checkboxOptionsMatch[1].split(',').map(opt => opt.trim().replace(/['"]/g, '')).filter(opt => opt) : [];
          
          // Extract checkboxValues
          const checkboxValuesMatch = section.match(/checkboxValues:\s*\{([^}]*)\}/);
          const checkboxValues = {};
          if (checkboxValuesMatch) {
            const valuesStr = checkboxValuesMatch[1];
            const valuePairs = valuesStr.split(',');
            valuePairs.forEach(pair => {
              const [key, value] = pair.split(':');
              if (key && value) {
                const cleanKey = key.trim().replace(/['"]/g, '');
                const cleanValue = value.trim() === 'true';
                checkboxValues[cleanKey] = cleanValue;
              }
            });
          }
          
          // Extract items (look for both 'item:' and 'items:' patterns, with single quotes)
          const itemsMatch = section.match(/(?:'item'|'items'|item|items):\s*\[([^\]]*)\]/);
          const items = [];
          if (itemsMatch) {
            const itemsStr = itemsMatch[1];
            // Split items by looking for {'text': or {text: or {'title': or {title: patterns
            let itemStart = Math.max(
              itemsStr.indexOf("{'text':"), 
              itemsStr.indexOf('{text:'),
              itemsStr.indexOf("{'title':"), 
              itemsStr.indexOf('{title:')
            );
            while (itemStart !== -1) {
              let itemEnd = itemsStr.indexOf('}', itemStart);
              if (itemEnd === -1) break;
              
              const itemStr = itemsStr.substring(itemStart, itemEnd + 1);
              const item = parseCheckListItem(itemStr);
              if (item) items.push(item);
              
              const nextTextStart = Math.max(
                itemsStr.indexOf("{'text':", itemEnd + 1),
                itemsStr.indexOf('{text:', itemEnd + 1)
              );
              const nextTitleStart = Math.max(
                itemsStr.indexOf("{'title':", itemEnd + 1),
                itemsStr.indexOf('{title:', itemEnd + 1)
              );
              itemStart = nextTextStart !== -1 && nextTitleStart !== -1 ? 
                Math.min(nextTextStart, nextTitleStart) : 
                Math.max(nextTextStart, nextTitleStart);
            }
          }
          
          return {
            header,
            items: items,
            checkboxOptions: checkboxOptions,
            checkboxValues: checkboxValues
          };
        } catch (error) {
          return null;
        }
      }).filter(Boolean);
      
      return parsedSections;
    } catch (error) {
      return [];
    }
  };
  
  // Parse individual checklist item - Updated to handle new structure
  const parseCheckListItem = (itemStr) => {
    try {
      
      // Extract title (preferred) or text (handle both 'text': and text: patterns)
      const titleMatch = itemStr.match(/(?:'title'|title):\s*([^,}]+)/);
      const textMatch = itemStr.match(/(?:'text'|text):\s*([^,}]+)/);
      const title = titleMatch ? titleMatch[1].trim().replace(/['"]/g, '') : 
                   textMatch ? textMatch[1].trim().replace(/['"]/g, '') : '';
      
      // Extract options (handle both 'options': and options: patterns)
      const optionsMatch = itemStr.match(/(?:'options'|options):\s*\[([^\]]*)\]/);
      const options = optionsMatch ? 
        optionsMatch[1].split(',').map(opt => opt.trim().replace(/['"]/g, '')).filter(opt => opt) : [];
      
      // Extract value (handle both 'value': and value: patterns)
      const valueMatch = itemStr.match(/(?:'value'|value):\s*([^,}]+)/);
      const value = valueMatch ? valueMatch[1].trim().replace(/['"]/g, '') : '';
      
      // Extract hasCheckbox (handle both 'hasCheckbox': and hasCheckbox: patterns)
      const hasCheckboxMatch = itemStr.match(/(?:'hasCheckbox'|hasCheckbox):\s*([^,}]+)/);
      const hasCheckbox = hasCheckboxMatch ? hasCheckboxMatch[1].trim() === 'true' : false;
      
      // Extract checkboxValue (handle both 'checkboxValue': and checkboxValue: patterns)
      const checkboxValueMatch = itemStr.match(/(?:'checkboxValue'|checkboxValue):\s*([^,}]+)/);
      const checkboxValue = checkboxValueMatch ? checkboxValueMatch[1].trim() === 'true' : false;
      
      // Extract isMainTitle (handle both 'isMainTitle': and isMainTitle: patterns)
      const isMainTitleMatch = itemStr.match(/(?:'isMainTitle'|isMainTitle):\s*([^,}]+)/);
      const isMainTitle = isMainTitleMatch ? isMainTitleMatch[1].trim() === 'true' : false;
      
      // Extract isStepHeader (handle both 'isStepHeader': and isStepHeader: patterns)
      const isStepHeaderMatch = itemStr.match(/(?:'isStepHeader'|isStepHeader):\s*([^,}]+)/);
      const isStepHeader = isStepHeaderMatch ? isStepHeaderMatch[1].trim() === 'true' : false;
      
      // Extract textField (handle both 'textField': and textField: patterns)
      const textFieldMatch = itemStr.match(/(?:'textField'|textField):\s*\{([^}]*)\}/);
      const textField = {};
      if (textFieldMatch) {
        const tfStr = textFieldMatch[1];
        const isTextBoxMatch = tfStr.match(/(?:'isTextBox'|isTextBox):\s*([^,}]+)/);
        textField.isTextBox = isTextBoxMatch ? isTextBoxMatch[1].trim() === 'true' : false;
        
        const hintTextMatch = tfStr.match(/(?:'hintText'|hintText):\s*([^,}]+)/);
        textField.hintText = hintTextMatch ? hintTextMatch[1].trim().replace(/['"]/g, '') : '';
        
        const valueTypeMatch = tfStr.match(/(?:'valueType'|valueType):\s*([^,}]+)/);
        textField.valueType = valueTypeMatch ? valueTypeMatch[1].trim().replace(/['"]/g, '') : '';
        
        const valueMatch = tfStr.match(/(?:'value'|value):\s*([^,}]+)/);
        textField.value = valueMatch ? valueMatch[1].trim().replace(/['"]/g, '') : '';
        
        const naMatch = tfStr.match(/(?:'na'|na):\s*([^,}]+)/);
        textField.na = naMatch ? naMatch[1].trim() === 'true' : false;
      }
      
      // Extract printDate (handle both 'printDate': and printDate: patterns)
      const printDateMatch = itemStr.match(/(?:'printDate'|printDate):\s*\{([^}]*)\}/);
      const printDate = {};
      if (printDateMatch) {
        const pdStr = printDateMatch[1];
        const isPrintDateMatch = pdStr.match(/(?:'isPrintDate'|isPrintDate):\s*([^,}]+)/);
        printDate.isPrintDate = isPrintDateMatch ? isPrintDateMatch[1].trim() === 'true' : false;
        
        const printValueMatch = pdStr.match(/(?:'printValue'|printValue):\s*([^,}]+)/);
        printDate.printValue = printValueMatch ? printValueMatch[1].trim().replace(/['"]/g, '') : '';
      }
      
      // Extract signature (handle both 'signature': and signature: patterns)
      const signatureMatch = itemStr.match(/(?:'signature'|signature):\s*\{([^}]*)\}/);
      const signature = {};
      if (signatureMatch) {
        const sigStr = signatureMatch[1];
        const isSignedMatch = sigStr.match(/(?:'isSigned'|isSigned):\s*([^,}]+)/);
        signature.isSigned = isSignedMatch ? isSignedMatch[1].trim() === 'true' : false;
        
        const signValueMatch = sigStr.match(/(?:'signValue'|signValue):\s*([^,}]+)/);
        signature.signValue = signValueMatch ? signValueMatch[1].trim().replace(/['"]/g, '') : '';
      }
      
      // Extract isOptional (handle both 'isOptional': and isOptional: patterns)
      const isOptionalMatch = itemStr.match(/(?:'isOptional'|isOptional):\s*([^,}]+)/);
      const isOptional = isOptionalMatch ? isOptionalMatch[1].trim() === 'true' : false;
      
      const result = {
        title: title,
        text: title, // Keep both for compatibility
        options,
        value,
        hasCheckbox,
        checkboxValue,
        isMainTitle,
        isStepHeader,
        textField,
        printDate,
        signature,
        isOptional
      };
      return result;
    } catch (error) {
      return null;
    }
  };

  // View Record
  const handleView = (rec) => {
    let mcq = [];
    try {
      let str = rec.mcqQuestionsList;
      if (typeof str === 'string') {
        let s = str.trim();
        try {
          mcq = JSON.parse(s);
          // Ensure sections use 'items' even if payload uses 'item'
          if (Array.isArray(mcq)) {
            mcq = mcq.map(sec => ({
              ...sec,
              items: Array.isArray(sec.items) ? sec.items : (Array.isArray(sec.item) ? sec.item : (sec.item ? sec.item : []))
            }));
          }
        } catch {
          // robust conversion for non-JSON-like strings
          try {
            // 1) Quote unquoted keys (allowing / - _ ( ) in names) - but be more careful
            s = s.replace(/([,{]\s*)([A-Za-z0-9_\/\-\(\)]+)(\s*:)/g, '$1"$2"$3');
            
            // 2) Convert single-quoted strings to double-quoted safely
            s = s.replace(/'((?:\\'|[^'])*)'/g, (m, inner) => {
              const unescaped = inner.replace(/\\'/g, "'");
              const escapedDouble = unescaped.replace(/"/g, '\\"');
              return '"' + escapedDouble + '"';
            });
            
            // 3) Fix booleans in known places
            s = s.replace(/(\"value\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"value\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isTextBox\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isTextBox\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isPrintDate\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isPrintDate\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isSigned\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isSigned\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isOptional\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isOptional\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"hasCheckbox\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"hasCheckbox\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"checkboxValue\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"checkboxValue\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"na\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"na\"\s*:\s*)\"false\"/g, '$1false');
            
            // 4) Remove trailing commas
            s = s.replace(/,\s*([}\]])/g, '$1');
            
            // 5) Fix the specific format issue with missing quotes around property names - but be more careful
            s = s.replace(/([A-Za-z0-9_\/\-\(\)]+):/g, '"$1":');
            
            // 6) Fix any remaining issues with quotes
            s = s.replace(/""/g, '"');
            
            // 7) Fix unescaped quotes in string values
            s = fixUnescapedQuotes(s);
            
            // 8) Additional aggressive fixes for severely malformed JSON
            // Fix the specific broken structure we're seeing
            s = s.replace(/"hintText"\s*:\s*",\\"valueType"\s*:\s*"/g, '"hintText":"","valueType":"');
            s = s.replace(/"printValue"\s*:\s*"},\\"signature"\s*:\s*"/g, '"printValue":"","signature":{"isSigned":false,"signValue":""');
            s = s.replace(/"signValue"\s*:\s*"},\\"isOptional"/g, '"signValue":"","isOptional"');
            
            // Fix cases where the structure is completely broken
            s = s.replace(/"textField"\s*:\s*\{\s*"isTextBox"\s*:\s*false\s*,\s*"hintText"\s*:\s*"([^"]*)"\s*,\s*"valueType"\s*:\s*"([^"]*)"\s*\}\s*,\s*"printDate"\s*:\s*\{\s*"isPrintDate"\s*:\s*false\s*,\s*"printValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"signature"\s*:\s*\{\s*"isSigned"\s*:\s*false\s*,\s*"signValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"isOptional"\s*:\s*false/g, 
              '"textField":{"isTextBox":false,"hintText":"$1","valueType":"$2"},"printDate":{"isPrintDate":false,"printValue":"$3"},"signature":{"isSigned":false,"signValue":"$4"},"isOptional":false');
            
            // 9) Nuclear option - completely reconstruct the JSON structure
            // This handles the most severely malformed cases
            try {
              // Work with the original string, not the processed one
              const originalStr = str;
              
              // Try to extract the basic structure and rebuild it
              const headerMatch = originalStr.match(/'header'\s*:\s*'([^']*)'/);
              if (headerMatch) {
                const header = headerMatch[1];
                
                // Extract all items by looking for text patterns in the original single-quoted format
                const items = [];
                
                // Use a more robust approach - manually parse items with proper brace counting
                // Find the items array and parse each item individually
                const itemsArrayMatch = originalStr.match(/'item'\s*:\s*\[(.*)\]/);
                if (itemsArrayMatch) {
                  const itemsStr = itemsArrayMatch[1];
                  
                  // Split items by looking for the pattern: {'text':'...',...}
                  // We need to be careful with nested braces
                  const parsedItems = [];
                  let currentItem = '';
                  let braceCount = 0;
                  let inItem = false;
                  
                  for (let i = 0; i < itemsStr.length; i++) {
                    const char = itemsStr[i];
                    
                    if (char === '{' && !inItem) {
                      inItem = true;
                      braceCount = 1;
                      currentItem = char;
                    } else if (inItem) {
                      currentItem += char;
                      if (char === '{') {
                        braceCount++;
                      } else if (char === '}') {
                        braceCount--;
                        if (braceCount === 0) {
                          // We've found a complete item
                          parsedItems.push(currentItem);
                          currentItem = '';
                          inItem = false;
                        }
                      }
                    }
                  }
                  
                  
                  parsedItems.forEach((itemStr, index) => {
                    // Extract text from each item - handle apostrophes properly
                    let textContent = '';
                    const textStart = itemStr.indexOf("'text':");
                    if (textStart !== -1) {
                      const textValueStart = itemStr.indexOf("'", textStart + 7);
                      if (textValueStart !== -1) {
                        const remainingStr = itemStr.substring(textValueStart + 1);
                        const nextPropMatch = remainingStr.match(/','(?:options|value|textField|printDate|signature|isOptional)'/);
                        if (nextPropMatch) {
                          textContent = remainingStr.substring(0, nextPropMatch.index);
                        }
                      }
                    }
                    
                    if (textContent) {
                      // Clean up the text content
                      textContent = textContent.replace(/\\'/g, "'");
                      
                      
                      // Try to extract actual values from the original string
                      let value = "Yes"; // default
                      let isTextBox = false;
                      let hintText = "";
                      let valueType = "";
                      let isPrintDate = false;
                      let printValue = "";
                      let isSigned = false;
                      let signValue = "";
                      
                      // Extract properties from the individual item string
                      const valueMatch = itemStr.match(/'value'\s*:\s*'([^']*)'/);
                      if (valueMatch) {
                        value = valueMatch[1];
                      }
                      
                      // Look for textField properties in the item
                      const textFieldMatch = itemStr.match(/'textField'\s*:\s*\{([^}]*)\}/);
                      if (textFieldMatch) {
                        const tfStr = textFieldMatch[1];
                        const isTextBoxMatch = tfStr.match(/'isTextBox'\s*:\s*(true|false)/);
                        if (isTextBoxMatch) isTextBox = isTextBoxMatch[1] === 'true';
                        
                        const hintTextMatch = tfStr.match(/'hintText'\s*:\s*'([^']*)'/);
                        if (hintTextMatch) hintText = hintTextMatch[1];
                        
                        const valueTypeMatch = tfStr.match(/'valueType'\s*:\s*'([^']*)'/);
                        if (valueTypeMatch) valueType = valueTypeMatch[1];
                      }
                      
                      // Look for printDate properties in the item
                      const printDateMatch = itemStr.match(/'printDate'\s*:\s*\{([^}]*)\}/);
                      if (printDateMatch) {
                        const pdStr = printDateMatch[1];
                        const isPrintDateMatch = pdStr.match(/'isPrintDate'\s*:\s*(true|false)/);
                        if (isPrintDateMatch) isPrintDate = isPrintDateMatch[1] === 'true';
                        
                        const printValueMatch = pdStr.match(/'printValue'\s*:\s*'([^']*)'/);
                        if (printValueMatch) printValue = printValueMatch[1];
                      }
                      
                      // Look for signature properties in the item
                      const signatureMatch = itemStr.match(/'signature'\s*:\s*\{([^}]*)\}/);
                      if (signatureMatch) {
                        const sigStr = signatureMatch[1];
                        const isSignedMatch = sigStr.match(/'isSigned'\s*:\s*(true|false)/);
                        if (isSignedMatch) isSigned = isSignedMatch[1] === 'true';
                        
                        const signValueMatch = sigStr.match(/'signValue'\s*:\s*'([^']*)'/);
                        if (signValueMatch) signValue = signValueMatch[1];
                      }
                      
                      // Create a properly structured item with actual values
                      const item = {
                        text: textContent,
                        options: ["Yes", "No"],
                        value: value,
                        textField: {
                          isTextBox: isTextBox,
                          hintText: hintText,
                          valueType: valueType
                        },
                        printDate: {
                          isPrintDate: isPrintDate,
                          printValue: printValue
                        },
                        signature: {
                          isSigned: isSigned,
                          signValue: signValue
                        },
                        isOptional: false
                      };
                      items.push(item);
                    } else {
                    }
                  });
                }
                
                // Reconstruct the JSON with proper structure
                const reconstructed = [{
                  header: header,
                  items: items
                }];
                
                s = JSON.stringify(reconstructed);
                
                // Final cleanup - remove any remaining escaped quotes that shouldn't be there
                s = s.replace(/\\\\"/g, '"');
                s = s.replace(/\\"/g, '"');
                
              }
            } catch (reconstructError) {
            }
            
            mcq = JSON.parse(s);
          } catch (parseError) {
            // If still fails, try to manually parse the structure
            try {
              mcq = parseCustomMcqFormat(str);
            } catch (customError) {
              mcq = [];
            }
          }
        }
      } else if (Array.isArray(str)) {
        mcq = str.map(sec => ({
          ...sec,
          items: Array.isArray(sec.items) ? sec.items : (Array.isArray(sec.item) ? sec.item : (sec.item ? sec.item : []))
        }));
      }
    } catch {
      mcq = [];
    }
    const normalized = normalizeMcq(Array.isArray(mcq) ? mcq : []);
    setViewRecord({ ...rec, mcqQuestionsList: normalized });
    setShowViewModal(true);
  };

  // Edit Record
  const handleEdit = (rec) => {
    let mcq = [];
    try {
      let str = rec.mcqQuestionsList;
      if (typeof str === 'string') {
        let s = str.trim();
        try {
          mcq = JSON.parse(s);
          // Ensure sections use 'items' even if payload uses 'item'
          if (Array.isArray(mcq)) {
            mcq = mcq.map(sec => ({
              ...sec,
              items: Array.isArray(sec.items) ? sec.items : (Array.isArray(sec.item) ? sec.item : (sec.item ? sec.item : []))
            }));
          }
        } catch {
          // robust conversion for non-JSON-like strings
          try {
            // 1) Quote unquoted keys (allowing / - _ ( ) in names) - but be more careful
            s = s.replace(/([,{]\s*)([A-Za-z0-9_\/\-\(\)]+)(\s*:)/g, '$1"$2"$3');
            
            // 2) Convert single-quoted strings to double-quoted safely
            s = s.replace(/'((?:\\'|[^'])*)'/g, (m, inner) => {
              const unescaped = inner.replace(/\\'/g, "'");
              const escapedDouble = unescaped.replace(/"/g, '\\"');
              return '"' + escapedDouble + '"';
            });
            
            // 3) Fix booleans in known places
            s = s.replace(/(\"value\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"value\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isTextBox\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isTextBox\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isPrintDate\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isPrintDate\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isSigned\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isSigned\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"isOptional\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"isOptional\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"hasCheckbox\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"hasCheckbox\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"checkboxValue\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"checkboxValue\"\s*:\s*)\"false\"/g, '$1false');
            s = s.replace(/(\"na\"\s*:\s*)\"true\"/g, '$1true');
            s = s.replace(/(\"na\"\s*:\s*)\"false\"/g, '$1false');
            
            // 4) Remove trailing commas
            s = s.replace(/,\s*([}\]])/g, '$1');
            
            // 5) Fix the specific format issue with missing quotes around property names - but be more careful
            s = s.replace(/([A-Za-z0-9_\/\-\(\)]+):/g, '"$1":');
            
            // 6) Fix any remaining issues with quotes
            s = s.replace(/""/g, '"');
            
            // 7) Fix unescaped quotes in string values
            s = fixUnescapedQuotes(s);
            
            // 8) Additional aggressive fixes for severely malformed JSON
            // Fix the specific broken structure we're seeing
            s = s.replace(/"hintText"\s*:\s*",\\"valueType"\s*:\s*"/g, '"hintText":"","valueType":"');
            s = s.replace(/"printValue"\s*:\s*"},\\"signature"\s*:\s*"/g, '"printValue":"","signature":{"isSigned":false,"signValue":""');
            s = s.replace(/"signValue"\s*:\s*"},\\"isOptional"/g, '"signValue":"","isOptional"');
            
            // Fix cases where the structure is completely broken
            s = s.replace(/"textField"\s*:\s*\{\s*"isTextBox"\s*:\s*false\s*,\s*"hintText"\s*:\s*"([^"]*)"\s*,\s*"valueType"\s*:\s*"([^"]*)"\s*\}\s*,\s*"printDate"\s*:\s*\{\s*"isPrintDate"\s*:\s*false\s*,\s*"printValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"signature"\s*:\s*\{\s*"isSigned"\s*:\s*false\s*,\s*"signValue"\s*:\s*"([^"]*)"\s*\}\s*,\s*"isOptional"\s*:\s*false/g, 
              '"textField":{"isTextBox":false,"hintText":"$1","valueType":"$2"},"printDate":{"isPrintDate":false,"printValue":"$3"},"signature":{"isSigned":false,"signValue":"$4"},"isOptional":false');
            
            // 9) Nuclear option - completely reconstruct the JSON structure
            // This handles the most severely malformed cases
            try {
              // Work with the original string, not the processed one
              const originalStr = str;
              
              // Try to extract the basic structure and rebuild it
              const headerMatch = originalStr.match(/'header'\s*:\s*'([^']*)'/);
              if (headerMatch) {
                const header = headerMatch[1];
                
                // Extract all items by looking for text patterns in the original single-quoted format
                const items = [];
                
                // Use a more robust approach - manually parse items with proper brace counting
                // Find the items array and parse each item individually
                const itemsArrayMatch = originalStr.match(/'item'\s*:\s*\[(.*)\]/);
                if (itemsArrayMatch) {
                  const itemsStr = itemsArrayMatch[1];
                  
                  // Split items by looking for the pattern: {'text':'...',...}
                  // We need to be careful with nested braces
                  const parsedItems = [];
                  let currentItem = '';
                  let braceCount = 0;
                  let inItem = false;
                  
                  for (let i = 0; i < itemsStr.length; i++) {
                    const char = itemsStr[i];
                    
                    if (char === '{' && !inItem) {
                      inItem = true;
                      braceCount = 1;
                      currentItem = char;
                    } else if (inItem) {
                      currentItem += char;
                      if (char === '{') {
                        braceCount++;
                      } else if (char === '}') {
                        braceCount--;
                        if (braceCount === 0) {
                          // We've found a complete item
                          parsedItems.push(currentItem);
                          currentItem = '';
                          inItem = false;
                        }
                      }
                    }
                  }
                  
                  
                  parsedItems.forEach((itemStr, index) => {
                    // Extract text from each item - handle apostrophes properly
                    let textContent = '';
                    const textStart = itemStr.indexOf("'text':");
                    if (textStart !== -1) {
                      const textValueStart = itemStr.indexOf("'", textStart + 7);
                      if (textValueStart !== -1) {
                        const remainingStr = itemStr.substring(textValueStart + 1);
                        const nextPropMatch = remainingStr.match(/','(?:options|value|textField|printDate|signature|isOptional)'/);
                        if (nextPropMatch) {
                          textContent = remainingStr.substring(0, nextPropMatch.index);
                        }
                      }
                    }
                    
                    if (textContent) {
                      // Clean up the text content
                      textContent = textContent.replace(/\\'/g, "'");
                      
                      
                      // Try to extract actual values from the original string
                      let value = "Yes"; // default
                      let isTextBox = false;
                      let hintText = "";
                      let valueType = "";
                      let isPrintDate = false;
                      let printValue = "";
                      let isSigned = false;
                      let signValue = "";
                      
                      // Extract properties from the individual item string
                      const valueMatch = itemStr.match(/'value'\s*:\s*'([^']*)'/);
                      if (valueMatch) {
                        value = valueMatch[1];
                      }
                      
                      // Look for textField properties in the item
                      const textFieldMatch = itemStr.match(/'textField'\s*:\s*\{([^}]*)\}/);
                      if (textFieldMatch) {
                        const tfStr = textFieldMatch[1];
                        const isTextBoxMatch = tfStr.match(/'isTextBox'\s*:\s*(true|false)/);
                        if (isTextBoxMatch) isTextBox = isTextBoxMatch[1] === 'true';
                        
                        const hintTextMatch = tfStr.match(/'hintText'\s*:\s*'([^']*)'/);
                        if (hintTextMatch) hintText = hintTextMatch[1];
                        
                        const valueTypeMatch = tfStr.match(/'valueType'\s*:\s*'([^']*)'/);
                        if (valueTypeMatch) valueType = valueTypeMatch[1];
                      }
                      
                      // Look for printDate properties in the item
                      const printDateMatch = itemStr.match(/'printDate'\s*:\s*\{([^}]*)\}/);
                      if (printDateMatch) {
                        const pdStr = printDateMatch[1];
                        const isPrintDateMatch = pdStr.match(/'isPrintDate'\s*:\s*(true|false)/);
                        if (isPrintDateMatch) isPrintDate = isPrintDateMatch[1] === 'true';
                        
                        const printValueMatch = pdStr.match(/'printValue'\s*:\s*'([^']*)'/);
                        if (printValueMatch) printValue = printValueMatch[1];
                      }
                      
                      // Look for signature properties in the item
                      const signatureMatch = itemStr.match(/'signature'\s*:\s*\{([^}]*)\}/);
                      if (signatureMatch) {
                        const sigStr = signatureMatch[1];
                        const isSignedMatch = sigStr.match(/'isSigned'\s*:\s*(true|false)/);
                        if (isSignedMatch) isSigned = isSignedMatch[1] === 'true';
                        
                        const signValueMatch = sigStr.match(/'signValue'\s*:\s*'([^']*)'/);
                        if (signValueMatch) signValue = signValueMatch[1];
                      }
                      
                      // Create a properly structured item with actual values
                      const item = {
                        text: textContent,
                        options: ["Yes", "No"],
                        value: value,
                        textField: {
                          isTextBox: isTextBox,
                          hintText: hintText,
                          valueType: valueType
                        },
                        printDate: {
                          isPrintDate: isPrintDate,
                          printValue: printValue
                        },
                        signature: {
                          isSigned: isSigned,
                          signValue: signValue
                        },
                        isOptional: false
                      };
                      items.push(item);
                    } else {
                    }
                  });
                }
                
                // Reconstruct the JSON with proper structure
                const reconstructed = [{
                  header: header,
                  items: items
                }];
                
                s = JSON.stringify(reconstructed);
                
                // Final cleanup - remove any remaining escaped quotes that shouldn't be there
                s = s.replace(/\\\\"/g, '"');
                s = s.replace(/\\"/g, '"');
                
              }
            } catch (reconstructError) {
            }
            
            mcq = JSON.parse(s);
          } catch (parseError) {
            // If still fails, try to manually parse the structure
            try {
              mcq = parseCustomMcqFormat(str);
            } catch (customError) {
              mcq = [];
            }
          }
        }
      } else if (Array.isArray(str)) {
        mcq = str.map(sec => ({
          ...sec,
          items: Array.isArray(sec.items) ? sec.items : (Array.isArray(sec.item) ? sec.item : (sec.item ? sec.item : []))
        }));
      }
    } catch {
      mcq = [];
    }
    // Do NOT fallback to static; use API only in edit flow
    const normalized = normalizeMcq(Array.isArray(mcq) ? mcq : []);
    setEditRecord({ ...rec, formUuid: ensureFormUuid(rec?.formUuid), mcqQuestionsList: normalized });
    setShowEditModal(true);
    setTimeout(() => {
      // Load main signature - Updated to match UKPN Reactive with proper scaling
      const sigRef = editSigCanvasRef.current;
      if (sigRef) {
        sigRef.clear();
        if (rec.signature) {
          let sigSrc = rec.signature;
          
          // Ensure proper data URL format
          if (typeof sigSrc === 'string') {
            if (!sigSrc.startsWith('data:image')) {
              sigSrc = 'data:image/png;base64,' + sigSrc;
            }
          }
          
          const img = new window.Image();
          img.onload = () => {
            if (sigRef) {
              const canvas = sigRef.getCanvas();
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              
              // Draw the image with proper scaling to maintain aspect ratio (like UKPN Reactive)
              const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
              const x = (canvas.width - img.width * scale) / 2;
              const y = (canvas.height - img.height * scale) / 2;
              
              ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
            }
          };
          img.onerror = () => {
            console.error('Failed to load main signature image, Source:', sigSrc.substring(0, 100) + '...');
          };
          img.src = sigSrc;
        } else if (sigRef) {
          sigRef.clear();
        }
      }

      // Load existing item signatures - Updated to match UKPN Reactive with proper scaling
      normalized.forEach((section, sectionIdx) => {
        if (Array.isArray(section.items)) {
          section.items.forEach((item, itemIdx) => {
            if (item.signature?.isSigned && item.signature?.signValue) {
              const key = `${sectionIdx}-${itemIdx}`;
              setTimeout(() => {
                const canvasRef = editItemSignatureRefs.current[key];
                if (canvasRef && canvasRef.getCanvas) {
                  canvasRef.clear();
                  let sigSrc = item.signature.signValue;
                  
                  // Ensure proper data URL format
                  if (typeof sigSrc === 'string') {
                    if (!sigSrc.startsWith('data:image')) {
                      sigSrc = 'data:image/png;base64,' + sigSrc;
                    }
                  }
                  
                  if (sigSrc.startsWith('data:image')) {
                    const img = new window.Image();
                    img.onload = () => {
                      if (canvasRef && canvasRef.getCanvas) {
                        const canvas = canvasRef.getCanvas();
                        const ctx = canvas.getContext('2d');
                        ctx.clearRect(0, 0, canvas.width, canvas.height);
                        
                        // Draw the image with proper scaling to maintain aspect ratio (like UKPN Reactive)
                        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
                        const x = (canvas.width - img.width * scale) / 2;
                        const y = (canvas.height - img.height * scale) / 2;
                        
                        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
                      }
                    };
                    img.onerror = () => {
                      console.error(`Failed to load item signature image for ${key}, Source:`, sigSrc.substring(0, 100) + '...');
                    };
                    img.src = sigSrc;
                  } else if (canvasRef) {
                    canvasRef.clear();
                  }
                } else {
                  // Retry after another delay
                  setTimeout(() => {
                    const retryCanvasRef = editItemSignatureRefs.current[key];
                    if (retryCanvasRef && retryCanvasRef.getCanvas) {
                      retryCanvasRef.clear();
                      let sigSrc = item.signature.signValue;
                      
                      if (typeof sigSrc === 'string') {
                        if (!sigSrc.startsWith('data:image')) {
                          sigSrc = 'data:image/png;base64,' + sigSrc;
                        }
                      }
                      
                      if (sigSrc.startsWith('data:image')) {
                        const img = new window.Image();
                        img.onload = () => {
                          if (retryCanvasRef && retryCanvasRef.getCanvas) {
                            const canvas = retryCanvasRef.getCanvas();
                            const ctx = canvas.getContext('2d');
                            ctx.clearRect(0, 0, canvas.width, canvas.height);
                            
                            // Draw the image with proper scaling to maintain aspect ratio
                            const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
                            const x = (canvas.width - img.width * scale) / 2;
                            const y = (canvas.height - img.height * scale) / 2;
                            
                            ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
                          }
                        };
                        img.onerror = () => {
                          console.error(`Failed to load item signature image on retry for ${key}`);
                        };
                        img.src = sigSrc;
                      }
                    } else {
                    }
                  }, 1000);
                }
              }, 500); // Use 500ms like UKPN Reactive
            }
          });
        }
      });
    }, 500); // Use 500ms like UKPN Reactive
  };

  const handleEditSave = async () => {
    // Prevent double submission
    if (savingRef.current || isSaving) {
      return;
    }
    savingRef.current = true;
    setIsSaving(true);
    
    if (!editRecord || !editRecord.inspector || !editRecord.employerNo || !editRecord.pasmaCardNo || !editRecord.inspectionDate || !editRecord.towerNo || !editRecord.platformHeight || !editRecord.projectJobNo || !editRecord.reasonForTower) {
      toast.error("All required fields must be filled");
      savingRef.current = false;
      setIsSaving(false);
      return;
    }
    
    // Force save all item signatures from canvas refs before saving (like UKPN Reactive)
    const updatedSections = [...editRecord.mcqQuestionsList];
    updatedSections.forEach((section, sectionIdx) => {
      if (Array.isArray(section.items)) {
        section.items.forEach((item, itemIdx) => {
          if (item.signature?.isSigned) {
            const key = `${sectionIdx}-${itemIdx}`;
            const canvasRef = editItemSignatureRefs.current[key];
            if (canvasRef && !canvasRef.isEmpty()) {
              const canvas = canvasRef.getCanvas();
              const sig = canvas.toDataURL("image/png");
              updatedSections[sectionIdx].items[itemIdx].signature.signValue = sig;
            }
          }
        });
      }
    });
    
    // Wait a bit for state updates, then save (like UKPN Reactive)
    setTimeout(async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        
        // Get the original record data to preserve structure
        const originalRecord = records.find(r => r._id === editRecord._id);
        if (!originalRecord) {
          toast.error("Original record not found");
          savingRef.current = false;
          setIsSaving(false);
          return;
        }
        
        // Get the latest signatures from refs one more time to ensure we have them (like UKPN Reactive)
        const finalSections = [...updatedSections];
        finalSections.forEach((section, sectionIdx) => {
          if (Array.isArray(section.items)) {
            section.items.forEach((item, itemIdx) => {
              if (item.signature?.isSigned) {
                const key = `${sectionIdx}-${itemIdx}`;
                const canvasRef = editItemSignatureRefs.current[key];
                if (canvasRef && !canvasRef.isEmpty()) {
                  const sig = canvasRef.getCanvas().toDataURL("image/png");
                  finalSections[sectionIdx].items[itemIdx].signature.signValue = sig;
                }
              }
            });
          }
        });
        
        // Create form data structure with updated signatures
        const formData = {
          ...editRecord,
          mcqQuestionsList: finalSections,
          inspectionDate: convertDateForSave(editRecord.inspectionDate)
        };
        
        
        // Update only the value fields in the original data structure
        const recordToSave = updateOnlyValueFields(originalRecord, formData);
        
        // Get user details for saving firstName and lastName
        const user = appUsers.find(u => u._id === editRecord.userId);
        recordToSave.addedByFirstName = user ? user.firstName : '';
        recordToSave.addedByLastName = user ? user.lastName : '';
        recordToSave.formUuid = ensureFormUuid(editRecord.formUuid);
        
        // Fix signature if it's an array - convert to string
        if (Array.isArray(recordToSave.signature)) {
          recordToSave.signature = recordToSave.signature[0] || '';
        }
        
        // Validate the data before sending
        if (!editRecord._id) {
          console.error('Missing record ID');
          toast.error('Missing record ID');
          savingRef.current = false;
          setIsSaving(false);
          return;
        }
        
        const res = await fetch(baseUrl + `/api/pre-fabricated/${editRecord._id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(recordToSave)
        });
          
        if (!res.ok) {
          const errorText = await res.text();
          console.error('Edit API Error Response:', errorText);
          toast.error(`Failed to update record: ${res.status} ${res.statusText}`);
          savingRef.current = false;
          setIsSaving(false);
          return;
        }
          
        const data = await res.json();
        
        if (res.ok && data.success) {
          // Update records with the API response data
          const updatedRecord = data.data.data;
          if (updatedRecord) {
            setRecords(records.map(r => r._id === editRecord._id ? updatedRecord : r));
          }
          
          // Refetch all records to ensure data consistency
          try {
            const refreshRes = await fetch(baseUrl + "/api/pre-fabricated", {
              headers: { "Authorization": `Bearer ${token}` }
            });
            const refreshData = await refreshRes.json();
            if (refreshRes.ok && refreshData.success && Array.isArray(refreshData.data)) {
              setRecords(refreshData.data);
            }
          } catch (refreshError) {
            console.warn('Failed to refresh records:', refreshError);
          }
          
          setShowEditModal(false);
          toast.success("Record updated!");
        } else {
          console.error('Edit failed:', { status: res.status, data });
          toast.error(data.message || `Failed to update (Status: ${res.status})`);
        }
      } catch {
        toast.error("Failed to update");
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    }, 100); // Use 100ms like UKPN Reactive
  };

  // Delete Record
  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.projectJobNo || '').trim();

    if (!locationForUpload) {
      toast.error('Project Job No is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/pre-fabricated/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecords(records.filter(r => r._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Modern color scheme
      const primaryColor = [139, 22, 52]; // #8b1634
      const secondaryColor = [233, 207, 214]; // #e9cfd6
      
      // Helper function to draw modern rounded rectangle
      const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
        if (hasShadow) {
          doc.setFillColor(0, 0, 0, 0.1);
          doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
        }
        doc.setFillColor(...fillColor);
        doc.setDrawColor(...strokeColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(x, y, width, height, radius, radius, 'FD');
      };
      
      // ---- HEADER SECTION ----
      const headerHeight = 30;
      drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);
      
      // ---- ADD HIGH-RES LOGO ----
      const addLogoToPDF = () => {
        return new Promise((resolve) => {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = function () {
            try {
              const logoWidth = 70;
              const aspectRatio = logoImg.height / logoImg.width;
              const logoHeight = logoWidth * aspectRatio;
              const logoX = 10;
              const logoY = (headerHeight - logoHeight) / 2;
              
              const canvas = document.createElement('canvas');
              const scale = 2.5;
              canvas.width = logoImg.width * scale;
              canvas.height = logoImg.height * scale;
              const ctx = canvas.getContext('2d');
              ctx.scale(scale, scale);
              ctx.drawImage(logoImg, 0, 0);
              
              const highResDataUrl = canvas.toDataURL('image/png', 1.0);
              doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
            } catch (error) {
              doc.setFillColor(255, 255, 255);
              doc.rect(10, 6, 60, 15, 'F');
              doc.setFont("helvetica", "bold");
              doc.setFontSize(10);
              doc.setTextColor(0, 0, 0);
              doc.text("TAYLOR TECHNOLOGY", 15, 12);
              doc.text("SYSTEMS LIMITED", 15, 17);
            }
            resolve();
          };
          logoImg.onerror = function () {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
            resolve();
          };
          logoImg.src = onsiteproLogo;
        });
      };
      
      await addLogoToPDF();
      
      // ---- HEADER TEXT ----
      const titleX = 90;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text("Pre-Fabricated Tower Inspection Report", titleX, 13);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);
      
      let yPosition = headerHeight + 10;
      
      // ---- Section 1: Basic Information ----
      doc.setFillColor(139, 22, 52);
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      
      doc.rect(10, yPosition, pageWidth - 20, 8, "F");
      doc.text("1", 14, yPosition + 5.5);
      doc.text("Basic Information", 22, yPosition + 5.5);
      yPosition += 8;
      
      autoTable(doc, {
        startY: yPosition,
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 65 },
          2: { cellWidth: 30 },
          3: { cellWidth: 45 },
        },
        head: [
          [
            { content: "Inspector:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.inspector || "", styles: { halign: "center" } },
            { content: "Employer No:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.employerNo || "", styles: { halign: "center" } },
          ],
          [
            { content: "PASMA Card No:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.pasmaCardNo || "", styles: { halign: "center" } },
            { content: "User Name 1:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.userName1 || "", styles: { halign: "center" } },
          ],
          [
            { content: "Employer No 1:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.employerNo1 || "", styles: { halign: "center" } },
            { content: "PASMA Card No 1:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.pasmaCardNo1 || "", styles: { halign: "center" } },
          ],
          [
            { content: "User Name 2:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.userName2 || "", styles: { halign: "center" } },
            { content: "Employer No 2:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.employerNo2 || "", styles: { halign: "center" } },
          ],
          [
            { content: "PASMA Card No 2:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.pasmaCardNo2 || "", styles: { halign: "center" } },
            { content: "Inspection Date:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.inspectionDate || "", styles: { halign: "center" } },
          ],
          [
            { content: "Tower No:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.towerNo || "", styles: { halign: "center" } },
            { content: "Platform Height:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.platformHeight || "", styles: { halign: "center" } },
          ],
          [
            { content: "Project Job No:", styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: record.projectJobNo || "", styles: { halign: "center" } },
            { content: "Reason For Tower:", styles: { fillColor: [230, 230, 230], fontStyle: "bold", halign: "center" } },
            { content: record.reasonForTower || "", styles: { halign: "center" } },
          ],
        ],
        body: [],
        styles: {
          font: "helvetica",
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          valign: "middle",
        },
        theme: "grid",
        tableWidth: pageWidth - 20,
        margin: { left: 10 },
        headStyles: {
          fillColor: [245, 245, 245],
          textColor: [0, 0, 0],
          lineWidth: 0.2,
        },
        bodyStyles: {
          lineWidth: 0.2,
          textColor: [0, 0, 0],
        },
      });
      
      yPosition = doc.lastAutoTable.finalY + 2;
      
      // ---- Section 2: MCQ Questions List ----
      let mcqQuestionsList = [];
      try {
        let str = record.mcqQuestionsList;
        if (typeof str === 'string') {
          // Remove outer quotes if present
          str = str.trim();
          if (str.startsWith('"') && str.endsWith('"')) {
            str = str.slice(1, -1);
          }
          
          // Try to parse as JSON first
          try {
            mcqQuestionsList = JSON.parse(str);
            if (!Array.isArray(mcqQuestionsList)) {
              mcqQuestionsList = [];
            }
          } catch (parseError) {
            // If JSON parsing fails, try JSON5 as fallback
            try {
              mcqQuestionsList = JSON5.parse(str);
              if (!Array.isArray(mcqQuestionsList)) {
                mcqQuestionsList = [];
              }
            } catch (json5Error) {
              mcqQuestionsList = [];
            }
          }
        } else if (Array.isArray(str)) {
          mcqQuestionsList = str;
        }
      } catch (err) {
        mcqQuestionsList = [];
      }
      
      // Normalize MCQ data
      const normalizedMcq = normalizeMcq(mcqQuestionsList);
      
      if (normalizedMcq && normalizedMcq.length > 0) {
        for (let sectionIdx = 0; sectionIdx < normalizedMcq.length; sectionIdx++) {
          const section = normalizedMcq[sectionIdx];
          // Check if we need a new page
          if (yPosition + 50 > pageHeight - 30) {
            doc.addPage();
            yPosition = 20;
          }
          
          // Section Header
          doc.setFillColor(139, 22, 52);
          doc.setTextColor(255, 255, 255);
          doc.setFont("helvetica", "bold");
          doc.rect(10, yPosition, pageWidth - 20, 8, "F");
          doc.text(`${sectionIdx + 2}`, 14, yPosition + 5.5);
          doc.text(section.header || `Section ${sectionIdx + 1}`, 22, yPosition + 5.5);
          yPosition += 8;
          
          // MCQ Items - Handle both 'item' (singular) and 'items' (plural)
          const sectionItems = section.item || section.items || [];
          if (Array.isArray(sectionItems) && sectionItems.length > 0) {
            // Filter items and build table rows with all fields
            const filteredItems = sectionItems.filter(item => !item.isMainTitle && !item.isStepHeader);
            
            if (filteredItems.length > 0) {
              // Helper function to add signature with proper sizing
              const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
                if (!signatureData) return;
                
                try {
                  let sigSrc = signatureData;
                  if (!sigSrc.startsWith('data:image')) {
                    sigSrc = 'data:image/png;base64,' + sigSrc;
                  }
                  
                  return new Promise((resolve) => {
                    const sigImg = new Image();
                    sigImg.crossOrigin = 'anonymous';
                    
                    sigImg.onload = () => {
                      try {
                        const aspectRatio = sigImg.width / sigImg.height;
                        let imgWidth = maxWidth;
                        let imgHeight = maxWidth / aspectRatio;
                        
                        if (imgHeight > maxHeight) {
                          imgHeight = maxHeight;
                          imgWidth = maxHeight * aspectRatio;
                        }
                        
                        imgWidth = Math.min(imgWidth, maxWidth);
                        imgHeight = Math.min(imgHeight, maxHeight);
                        
                        const scale = 4;
                        const canvas = document.createElement('canvas');
                        canvas.width = Math.round(imgWidth * scale);
                        canvas.height = Math.round(imgHeight * scale);
                        const ctx = canvas.getContext('2d');
                        ctx.imageSmoothingEnabled = true;
                        ctx.imageSmoothingQuality = 'high';
                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(sigImg, 0, 0, sigImg.width, sigImg.height, 0, 0, canvas.width, canvas.height);
                        
                        // Apply contrast enhancement to make signature darker
                        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        const data = imageData.data;
                        for (let i = 0; i < data.length; i += 4) {
                          // Only darken non-white pixels (signature lines)
                          const r = data[i];
                          const g = data[i + 1];
                          const b = data[i + 2];
                          // If not white/very light, darken it
                          if (r < 250 || g < 250 || b < 250) {
                            data[i] = Math.max(0, r - 20);     // Darken red
                            data[i + 1] = Math.max(0, g - 20); // Darken green
                            data[i + 2] = Math.max(0, b - 20); // Darken blue
                          }
                        }
                        ctx.putImageData(imageData, 0, 0);
                        
                        const highResDataUrl = canvas.toDataURL('image/png', 1.0);
                        doc.addImage(highResDataUrl, 'PNG', x, y, imgWidth, imgHeight, '', 'NONE');
                      } catch (err) {
                        console.error('Error adding signature to PDF:', err);
                        try {
                          doc.addImage(sigSrc, 'PNG', x, y, maxWidth, maxHeight);
                        } catch (fallbackErr) {
                          console.error('Fallback signature addition also failed:', fallbackErr);
                        }
                      }
                      resolve();
                    };
                    
                    sigImg.onerror = () => {
                      console.error('Error loading signature image');
                      resolve();
                    };
                    
                    sigImg.src = sigSrc;
                  });
                } catch (err) {
                  console.error('Signature processing error:', err);
                }
              };
              
              // Store row positions for signature drawing
              const signaturePositions = [];
              
              // Build table body with additional columns
              const tableBody = filteredItems.map(item => {
                const textFieldValue = getMcqPrintNameValue(item);
                const printDateValue = item.printDate?.printValue || '';
                const hasSignature = item.signature?.isSigned && item.signature?.signValue;
                
                return [
                  { 
                    content: item.title || item.text || '', 
                    styles: { halign: "left" } 
                  },
                  { 
                    content: '', 
                    styles: { halign: "center" } 
                  },
                  {
                    content: textFieldValue || '-',
                    styles: { halign: "left", fontSize: 8 }
                  },
                  {
                    content: printDateValue || '-',
                    styles: { halign: "center", fontSize: 8 }
                  },
                  {
                    content: hasSignature ? '✓' : '-',
                    styles: { halign: "center", fontSize: 8 }
                  }
                ];
              });
              
              // Calculate column widths to match header width (pageWidth - 20)
              const availableWidth = pageWidth - 20;
              const fixedColumnsWidth = 25 + 40 + 30 + 25; // Status + Print Name + Date + Signature
              const itemColumnWidth = availableWidth - fixedColumnsWidth;
              
              autoTable(doc, {
                startY: yPosition,
                head: [
                  [
                    { content: "Item", styles: { fillColor: [240, 240, 240], fontStyle: "bold" } },
                    { content: "Status", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
                    { content: "Print Name", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
                    { content: "Date", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
                    { content: "Signature", styles: { fillColor: [240, 240, 240], fontStyle: "bold", halign: "center" } },
                  ],
                ],
                body: tableBody,
                columnStyles: {
                  0: { cellWidth: itemColumnWidth }, // Item column - fills remaining space
                  1: { cellWidth: 25 }, // Status
                  2: { cellWidth: 40 }, // Print Name
                  3: { cellWidth: 30 }, // Date
                  4: { cellWidth: 25 }, // Signature
                },
                didDrawCell: (data) => {
                  if (data.section === 'body') {
                    const item = filteredItems[data.row.index];
                    if (item) {
                      // Status column - checkmark
                      if (data.column.index === 1) {
                        const value = item.value;
                        const isCompleted = value === true || value === 'Yes' || value === 'YES' || value === 'true';
                        if (isCompleted) {
                          const cell = data.cell;
                          const centerX = cell.x + cell.width / 2;
                          const centerY = cell.y + cell.height / 2;
                          doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
                        }
                      }
                      
                      // Store signature column position
                      if (data.column.index === 4 && item.signature?.isSigned && item.signature?.signValue) {
                        signaturePositions.push({
                          item: item,
                          cell: data.cell
                        });
                      }
                    }
                  }
                },
                styles: {
                  font: "helvetica",
                  fontSize: 8,
                  cellPadding: 3,
                  halign: "left",
                  valign: "middle",
                },
                theme: "grid",
                tableWidth: pageWidth - 20,
                margin: { left: 10 },
                headStyles: {
                  textColor: [0, 0, 0],
                  fillColor: [245, 245, 245],
                  lineWidth: 0.2,
                },
                bodyStyles: {
                  lineWidth: 0.2,
                },
              });
              
              // Draw signatures for items that have them (after table is drawn)
              const signaturePromises = [];
              for (const { item, cell } of signaturePositions) {
                if (item.signature?.isSigned && item.signature?.signValue) {
                  const signatureX = cell.x + 2;
                  const signatureY = cell.y + 1;
                  const signatureMaxWidth = 20;
                  const signatureMaxHeight = 6;
                  
                  // Add signature drawing promise
                  signaturePromises.push(
                    addSignatureToPDF(item.signature.signValue, signatureX, signatureY, signatureMaxWidth, signatureMaxHeight)
                  );
                }
              }
              
              // Wait for all signatures to be drawn
              await Promise.all(signaturePromises);
              
              yPosition = doc.lastAutoTable.finalY + 2;
            }
          }
        }
      }
      
      // ---- Section for Signature ----
      if (record.signature) {
        if (yPosition + 60 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(139, 22, 52);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.rect(10, yPosition, pageWidth - 20, 10, "F");
        const sectionNum = normalizedMcq && normalizedMcq.length > 0 ? normalizedMcq.length + 2 : 2;
        doc.text(`Section ${sectionNum} : Signatories`, 15, yPosition + 7);
        yPosition += 18;
        
        // Signature box (half width)
        const boxWidth = (pageWidth - 20) / 2;
        const boxHeight = 50;
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(248, 248, 248);
        doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");
        
        // Label
        doc.setTextColor(139, 22, 52);
        doc.setFontSize(12);
        doc.text("Signature", 15, yPosition + 12);
        
        // Helper function to add signature with proper sizing
        const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
          if (!signatureData) return;
          
          try {
            let sigSrc = signatureData;
            if (!sigSrc.startsWith('data:image')) {
              sigSrc = 'data:image/png;base64,' + sigSrc;
            }
            
            return new Promise((resolve) => {
              const sigImg = new Image();
              sigImg.crossOrigin = 'anonymous';
              
              sigImg.onload = () => {
                try {
                  const aspectRatio = sigImg.width / sigImg.height;
                  let imgWidth = maxWidth;
                  let imgHeight = maxWidth / aspectRatio;
                  
                  if (imgHeight > maxHeight) {
                    imgHeight = maxHeight;
                    imgWidth = maxHeight * aspectRatio;
                  }
                  
                  imgWidth = Math.min(imgWidth, maxWidth);
                  imgHeight = Math.min(imgHeight, maxHeight);
                  
                  const scale = 4;
                  const canvas = document.createElement('canvas');
                  canvas.width = Math.round(imgWidth * scale);
                  canvas.height = Math.round(imgHeight * scale);
                  const ctx = canvas.getContext('2d');
                  ctx.imageSmoothingEnabled = true;
                  ctx.imageSmoothingQuality = 'high';
                  ctx.fillStyle = '#FFFFFF';
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.drawImage(sigImg, 0, 0, sigImg.width, sigImg.height, 0, 0, canvas.width, canvas.height);
                  
                  // Apply contrast enhancement to make signature darker
                  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                  const data = imageData.data;
                  for (let i = 0; i < data.length; i += 4) {
                    // Only darken non-white pixels (signature lines)
                    const r = data[i];
                    const g = data[i + 1];
                    const b = data[i + 2];
                    // If not white/very light, darken it
                    if (r < 250 || g < 250 || b < 250) {
                      data[i] = Math.max(0, r - 20);     // Darken red
                      data[i + 1] = Math.max(0, g - 20); // Darken green
                      data[i + 2] = Math.max(0, b - 20); // Darken blue
                    }
                  }
                  ctx.putImageData(imageData, 0, 0);
                  
                  const highResDataUrl = canvas.toDataURL('image/png', 1.0);
                  doc.addImage(highResDataUrl, 'PNG', x, y, imgWidth, imgHeight, '', 'NONE');
                } catch (err) {
                  console.error('Error adding signature to PDF:', err);
                  try {
                    doc.addImage(sigSrc, 'PNG', x, y, maxWidth, maxHeight);
                  } catch (fallbackErr) {
                    console.error('Fallback signature addition also failed:', fallbackErr);
                  }
                }
                resolve();
              };
              
              sigImg.onerror = () => {
                console.error('Error loading signature image');
                resolve();
              };
              
              sigImg.src = sigSrc;
            });
          } catch (err) {
            console.error('Signature processing error:', err);
          }
        };
        
        // Signature image constraints
        const signatureMaxWidth = 50;
        const signatureMaxHeight = 22;
        const signatureX = 15;
        const signatureY = yPosition + 15;
        
        await addSignatureToPDF(record.signature, signatureX, signatureY, signatureMaxWidth, signatureMaxHeight);
        yPosition += boxHeight + 10;
      }
      
      // ---- Footer Section ----
      const footerY = pageHeight - 25;
      drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
        20,
        footerY + 8
      );
      doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
      
      // Decorative border removed
      
      // Build filename and either save or upload
      const safeName = String(record.inspector || 'PreFabricated').replace(/[\\/:"*?<>|]+/g, '-');
      const safeDate = String(record.inspectionDate || 'Inspection').replace(/[\\/:"*?<>|]+/g, '-');
      
      let fileName;
      if (shouldUploadToSharePoint && uploadConfig) {
        fileName = buildSharePointFileName([
          record.inspector,
          uploadConfig.siteName,
          toDdMmYyyy(record.inspectionDate),
        ]);
      } else {
        fileName = `PreFabricated_${safeName}_${safeDate}.pdf`;
      }
      
      if (shouldUploadToSharePoint && uploadConfig) {
        try {
          const key = `${record._id || ''}|${record.inspector || ''}|${record.inspectionDate || ''}`;
          setUploadingKey(key);
          const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
          const dataUri = doc.output('datauristring');
          
          // Prepare SharePoint upload parameters
          const uploadParams = {
            fileName,
            fileBase64: dataUri,
            phaseType: uploadConfig.phaseType,
            phaseNumber: uploadConfig.phaseNumber,
            network: uploadConfig.network,
            siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "Pre-Fabricated"
          };
          
          const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
            method: 'POST',
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: JSON.stringify(uploadParams)
          });
          if (res.status !== 204 && !res.ok) {
            let msg = 'SharePoint upload failed';
            try {
              const out = await res.json();
              msg = out?.message || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          toast.success("PDF uploaded to SharePoint successfully!");
          if (uploadConfig.onSuccess) {
            uploadConfig.onSuccess();
          }
        } catch (err) {
          toast.error(err?.message || "SharePoint upload failed");
          if (uploadConfig.onError) {
            uploadConfig.onError(err);
          }
        } finally {
          setUploadingKey(null);
        }
      } else {
        doc.save(fileName);
        toast.success("Professional Pre-Fabricated Tower Inspection PDF generated successfully!");
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF');
    }
  };

  // MCQ Questions List handlers - Updated to match PowAssessmentPage structure
  const handleMcqChange = (sectionIdx, itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      const item = updatedSections[sectionIdx].items[itemIdx];
      updatedSections[sectionIdx].items[itemIdx] = {
        ...item,
        value,
        booleanValue: value === 'YES' ? true : value === 'NO' ? false : item.booleanValue
      };
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      const item = updatedSections[sectionIdx].items[itemIdx];
      updatedSections[sectionIdx].items[itemIdx] = {
        ...item,
        value,
        booleanValue: value === 'YES' ? true : value === 'NO' ? false : item.booleanValue
      };
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqCheckboxChange = (sectionIdx, checkboxKey, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].checkboxValues[checkboxKey] = value;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].checkboxValues[checkboxKey] = value;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqTextFieldChange = (sectionIdx, itemIdx, value, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].textFieldValue = value;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].textFieldValue = value;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqItemCheckboxChange = (sectionIdx, itemIdx, checked, isEdit = false) => {
    if (isEdit) {
      const updatedSections = [...editRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].checkboxValue = checked;
      setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
    } else {
      const updatedSections = [...newRecord.mcqQuestionsList];
      updatedSections[sectionIdx].items[itemIdx].checkboxValue = checked;
      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
    }
  };

  const handleMcqNAChange = (sectionIdx, itemIdx, checked, isEdit = false) => {
    if (isEdit) {
      const updated = [...editRecord.mcqQuestionsList];
      const it = updated[sectionIdx].items[itemIdx];
      updated[sectionIdx].items[itemIdx] = { ...it, textField: { ...it.textField, na: !!checked } };
      setEditRecord({ ...editRecord, mcqQuestionsList: updated });
    } else {
      const updated = [...newRecord.mcqQuestionsList];
      const it = updated[sectionIdx].items[itemIdx];
      updated[sectionIdx].items[itemIdx] = { ...it, textField: { ...it.textField, na: !!checked } };
      setNewRecord({ ...newRecord, mcqQuestionsList: updated });
    }
  };

  // Text handler
  const handleTextChange = (field, value, isEdit = false) => {
    if (isEdit) {
      setEditRecord({ ...editRecord, [field]: value });
    } else {
      setNewRecord({ ...newRecord, [field]: value });
    }
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>Pre-Fabricated Tower Inspection</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 0.9fr 1fr 0.9fr 1.2fr',
          background: '#f7fafc',
          padding: '12px 16px',
          borderBottom: '1px solid #e2e8f0',
          gap: '16px',
          alignItems: 'center'
        }}>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Inspector</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Employer No</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>PASMA Card No</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Inspection Date</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Tower No</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
          <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
        </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr 1fr 0.9fr 1fr 0.9fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.inspector || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.employerNo || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.pasmaCardNo || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.inspectionDate || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.towerNo || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                  <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(r.updatedAt || r.createdAt)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        {canEditDelete && (
                          <button 
                          onClick={()=>handleEdit(r)}
                          title="Edit"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                        )}
                      <button 
                        onClick={()=>generatePDF(r)}
                        title="Generate PDF"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                      </button>
                      {(() => { 
                        const k = `${r._id || ''}|${r.inspector || ''}|${r.inspectionDate || ''}`; 
                        const busy = uploadingKey === k; 
                        return (
                          <button 
                            onClick={()=>{
                              if (!busy) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType('phase');
                                setSharePointPhaseNumber('15');
                                setSharePointNetwork('');
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={busy}
                            title={busy ? "Uploading..." : "Upload to SharePoint"}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: busy ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                        ); 
                      })()}
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleDelete(r._id)}
                          title="Delete"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add Pre-Fabricated Tower Inspection</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-2 col-4">
                    <label>User ID</label>
                    <input type="text" className="form-control" value={newRecord.userId} onChange={e=>handleTextChange("userId", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Inspector *</label>
                    <input type="text" className="form-control" value={newRecord.inspector} onChange={e=>handleTextChange("inspector", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Employer No *</label>
                    <input type="text" className="form-control" value={newRecord.employerNo} onChange={e=>handleTextChange("employerNo", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>PASMA Card No *</label>
                    <input type="text" className="form-control" value={newRecord.pasmaCardNo} onChange={e=>handleTextChange("pasmaCardNo", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>User Name 1</label>
                    <input type="text" className="form-control" value={newRecord.userName1} onChange={e=>handleTextChange("userName1", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Employer No 1</label>
                    <input type="text" className="form-control" value={newRecord.employerNo1} onChange={e=>handleTextChange("employerNo1", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>PASMA Card No 1</label>
                    <input type="text" className="form-control" value={newRecord.pasmaCardNo1} onChange={e=>handleTextChange("pasmaCardNo1", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>User Name 2</label>
                    <input type="text" className="form-control" value={newRecord.userName2} onChange={e=>handleTextChange("userName2", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Employer No 2</label>
                    <input type="text" className="form-control" value={newRecord.employerNo2} onChange={e=>handleTextChange("employerNo2", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>PASMA Card No 2</label>
                    <input type="text" className="form-control" value={newRecord.pasmaCardNo2} onChange={e=>handleTextChange("pasmaCardNo2", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Inspection Date *</label>
                    <input type="date" className="form-control" value={newRecord.inspectionDate} onChange={e=>handleTextChange("inspectionDate", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Tower No *</label>
                    <input type="text" className="form-control" value={newRecord.towerNo} onChange={e=>handleTextChange("towerNo", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Platform Height *</label>
                    <input type="text" className="form-control" value={newRecord.platformHeight} onChange={e=>handleTextChange("platformHeight", e.target.value)} />
                  </div>
                  <div className="mb-2 col-4">
                    <label>Project Job No *</label>
                    <input type="text" className="form-control" value={newRecord.projectJobNo} onChange={e=>handleTextChange("projectJobNo", e.target.value)} />
                  </div>
                  <div className="mb-2 col-md-6">
                    <label>Reason for Tower *</label>
                    <textarea className="form-control" rows="3" value={newRecord.reasonForTower} onChange={e=>handleTextChange("reasonForTower", e.target.value)} />
                  </div>

                  {/* MCQ Sections - Updated to match PowAssessmentPage structure */}
                  <div className="mb-2 col-md-6">
                    <label className="fw-bold" style={{color:'#8b1634'}}>MCQ Questions List</label>
                    {Array.isArray(newRecord.mcqQuestionsList) && newRecord.mcqQuestionsList.map((section, sidx) => (
                      <div key={sidx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                        {section.header === 'Steps List' ? (
                          <div>
                            {/* Show main title from API data */}
                            <div className="fw-bold" style={{marginBottom:8, color:'#8b1634', fontSize:'18px'}}>
                              {section.mainTitle || 'Steps List'}
                            </div>
                          </div>
                        ) : (
                        <div className="fw-bold" style={{marginBottom:8}}>{section.header}</div>
                        )}
                        {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                          <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:16}}>
                            {section.checkboxOptions.map(opt => (
                              <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                <input type="checkbox" checked={!!section.checkboxValues?.[opt]} onChange={e=>handleMcqCheckboxChange(sidx, opt, e.target.checked)} style={{accentColor:'#8b1634'}} /> {opt}
                                </label>
                              ))}
                  </div>
                        )}
                        {Array.isArray(section.items) && section.items.map((item, iidx) => (
                          <div key={iidx} className="mb-2" style={{paddingLeft:8}}>
                                {item.isMainTitle ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:12, marginTop:8, }}>
                                    {item.title}
                                  </div>
                                ) : item.isStepHeader ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8, marginTop:12, paddingLeft:16}}>
                                    {item.title}
                                  </div>
                                ) : (
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6}}>
                                  <div style={{display:'flex', alignItems:'center', gap:12}}>
                                      {/* Show title and subtitle */}
                                      <div>
                                        <div className="fw-bold" style={{color:'#8b1634', fontSize:'1.1rem'}}>
                                          {item.title ? item.title.split(' - ')[0] : item.text}
                                        </div>
                                        {item.title && item.title.includes(' - ') && (
                                          <div className="fw-medium" style={{color:'#666', fontSize:'0.9rem'}}>
                                            {item.title.split(' - ')[1] || ''}
                                          </div>
                                        )}
                                      </div>
                                    
                                    {/* Show text input box when isTextBox: true */}
                              {item.textField?.isTextBox && (
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    style={{
                                      maxWidth: 320,
                                          fontSize: '16px',
                                      fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                      lineHeight: '1.5',
                                          padding: '8px 12px'
                                    }}
                                    placeholder={item.textField?.hintText || ''} 
                                        value={item.textFieldValue !== undefined ? item.textFieldValue : (item.textField?.value || '')} 
                                        onChange={e=>handleMcqTextFieldChange(sidx, iidx, e.target.value)} 
                                      />
                                    )}
                                </div>
                                  
                                  {/* Show checkboxes for items with hasCheckbox: true */}
                                  {item.hasCheckbox && (
                                    <input 
                                      type="checkbox" 
                                      checked={!!item.checkboxValue} 
                                      onChange={e=>handleMcqItemCheckboxChange(sidx, iidx, e.target.checked)}
                                      style={{accentColor:'#8b1634'}} 
                                    />
                                  )}
                  </div>
                                )}
                                
                                {/* Show radio buttons for items without hasCheckbox */}
                                {!item.hasCheckbox && !item.textField?.isTextBox && Array.isArray(item.options) && (
                                  <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center', paddingLeft: item.hasCheckbox ? 0 : 20}}>
                                    {item.options.map(opt => (
                                      <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                        <input type="radio" name={`add-${sidx}-${iidx}`} checked={item.value === opt} onChange={()=>handleMcqChange(sidx, iidx, opt)} style={{accentColor:'#8b1634'}} /> {opt}
                                      </label>
                                    ))}
                                  </div>
                                )}

                                {/* Show print date field */}
                              {item.printDate?.isPrintDate && (
                                <div style={{display:'flex', alignItems:'center', gap:12, marginTop:8}}>
                                    <label style={{fontSize:'14px', fontWeight:'500'}}>Date:</label>
                                  <input 
                                    type="date" 
                                    className="form-control" 
                                    style={{
                                      maxWidth: 200,
                                        fontSize: '16px',
                                      fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                      lineHeight: '1.5',
                                        padding: '8px 12px'
                                    }}
                                    value={item.printDate?.printValue ? item.printDate.printValue.split('/').reverse().join('-') : ''} 
                                    onChange={e=>{
                                      const updatedSections = [...newRecord.mcqQuestionsList];
                                        updatedSections[sidx].items[iidx].printDate.printValue = e.target.value.split('-').reverse().join('/');
                                      setNewRecord({ ...newRecord, mcqQuestionsList: updatedSections });
                                    }} 
                                  />
                                </div>
                              )}

                                {/* Show signature field */}
                              {item.signature?.isSigned && (
                                <div style={{display:'flex', flexDirection:'column', gap:8, marginTop:8}}>
                                    <label style={{fontSize:'14px', fontWeight:'500'}}>Signature:</label>
                                  <div style={{width:'300px', border:'2px solid #8b1634', borderRadius:'8px', background:'#e9cfd6', padding:'8px', minHeight:'80px'}}>
                                    <SignatureCanvas
                                      ref={el => {
                                        const key = `${sidx}-${iidx}`;
                                        itemSignatureRefs.current[key] = el;
                                      }}
                                      penColor="#8b1634"
                                      backgroundColor="#e9cfd6"
                                      clearOnResize={false}
                                      velocityFilterWeight={0.7}
                                      minWidth={1}
                                      maxWidth={2}
                                      dotSize={1}
                                      throttle={16}
                                      canvasProps={{ 
                                        width: 280, 
                                        height: 60, 
                                        style: { 
                                          width: '100%', 
                                          height: '60px', 
                                          border: 'none', 
                                          borderRadius: '8px', 
                                          background: '#e9cfd6', 
                                          display: 'block', 
                                          touchAction: 'none' 
                                        } 
                                      }}
                                      onEnd={() => saveItemSignature(sidx, iidx)}
                                    />
                                    <div style={{width:'100%', display:'flex', justifyContent:'flex-end', marginTop:'4px'}}>
                                      <button 
                                        type="button" 
                                        className="btn btn-outline-danger btn-sm rounded-pill px-2" 
                                        style={{background:'transparent', color:'#8b1634', borderColor:'#8b1634', fontSize:'12px'}} 
                                        onClick={() => clearItemSignature(sidx, iidx)}
                                      >
                                        Clear
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  <div className="mb-2 col-md-6">
                    <label className="form-label fw-semibold text-secondary">Signature</label>
                    <div style={{
                      width: '100%',
                      border: '2px solid #8b1634',
                      borderRadius: '18px',
                      background: '#e9cfd6',
                      padding: '0.5rem',
                      textAlign: 'left',
                      position: 'relative',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <div style={{
                        width: '100%',
                        height: '100px',
                        position: 'relative',
                        display: 'block'
                      }}>
                      <SignatureCanvas
                        ref={sigCanvasRef}
                        penColor="#8b1634"
                          backgroundColor="white"
                        clearOnResize={false}
                        velocityFilterWeight={0.7}
                        minWidth={1}
                        maxWidth={2.5}
                        dotSize={1.5}
                        throttle={16}
                          canvasProps={{
                            width: 500,
                            height: 80,
                            style: {
                              width: '100%',
                              height: '80px',
                              border: 'none',
                              borderRadius: '12px',
                              background: 'white',
                              display: 'block',
                              touchAction: 'none',
                              cursor: 'crosshair',
                              position: 'relative'
                            }
                          }}
                        onEnd={saveSignature}
                      />
                      </div>
                      <div style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '8px'
                      }}>
                        <span style={{
                          fontSize: '12px',
                          color: '#8b1634',
                          fontStyle: 'italic'
                        }}>
                          Draw your signature above
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm rounded-pill px-3"
                          style={{
                            background: 'transparent',
                            color: '#8b1634',
                            borderColor: '#8b1634'
                          }}
                          onClick={clearSignature}>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editRecord && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={adminEditModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>Edit Pre-Fabricated</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)}></button>
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Inspector *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.inspector || ''} onChange={e=>handleTextChange("inspector", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Employer No *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.employerNo || ''} onChange={e=>handleTextChange("employerNo", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>PASMA Card No *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.pasmaCardNo || ''} onChange={e=>handleTextChange("pasmaCardNo", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>User Name 1</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.userName1 || ''} onChange={e=>handleTextChange("userName1", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Employer No 1</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.employerNo1 || ''} onChange={e=>handleTextChange("employerNo1", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>PASMA Card No 1</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.pasmaCardNo1 || ''} onChange={e=>handleTextChange("pasmaCardNo1", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>User Name 2</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.userName2 || ''} onChange={e=>handleTextChange("userName2", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Employer No 2</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.employerNo2 || ''} onChange={e=>handleTextChange("employerNo2", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>PASMA Card No 2</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.pasmaCardNo2 || ''} onChange={e=>handleTextChange("pasmaCardNo2", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Inspection Date *</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRecord?.inspectionDate)}
                        onChange={(d) => handleTextChange("inspectionDate", d ? formatIsoDate(d) : "", true)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Tower No *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.towerNo || ''} onChange={e=>handleTextChange("towerNo", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Platform Height *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.platformHeight || ''} onChange={e=>handleTextChange("platformHeight", e.target.value, true)} />
                  </div>
                  <div className="col-12 col-md-4">
                    <label style={adminEditLabelStyle}>Project Job No *</label>
                    <input type="text" className="form-control" style={adminEditInputStyle} value={editRecord?.projectJobNo || ''} onChange={e=>handleTextChange("projectJobNo", e.target.value.replace(/[^0-9]/g, ''), true)} pattern="[0-9]*" inputMode="numeric" />
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Reason for Tower *</label>
                    <textarea className="form-control" style={adminEditInputStyle} rows="3" value={editRecord?.reasonForTower || ''} onChange={e=>handleTextChange("reasonForTower", e.target.value, true)} />
                  </div>

                  {/* MCQ Sections - Edit */}
                  <div className="mb-2 col-md-12">
                    <label style={{ ...adminEditLabelStyle, color: ADMIN_THEME, fontSize: 14, marginBottom: 10 }}>MCQ Questions List</label>
                    {Array.isArray(editRecord.mcqQuestionsList) && editRecord.mcqQuestionsList.map((section, sidx) => (
                      <div key={sidx} style={{ border: "1px solid #e8e8e8", borderRadius: 10, overflow: "hidden", marginBottom: 12 }}>
                        {section.header === 'Steps List' ? (
                          <div>
                            {/* Show main title from API data */}
                            <div className="fw-bold" style={{marginBottom:8, color:'#8b1634', fontSize:'18px'}}>
                              {section.mainTitle || 'Steps List'}
                            </div>
                          </div>
                        ) : (
                        <div style={{ padding: "10px 14px", background: "#f8f4f5", borderBottom: "1px solid #e8e8e8", fontWeight: 600, fontSize: 14, color: ADMIN_THEME }}>{section.header}</div>
                        )}
                        <div style={{ padding: "12px 14px" }}>
                        {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                          <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:16}}>
                            {section.checkboxOptions.map(opt => (
                              <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                <input type="checkbox" className="form-check-input theme-checkbox" checked={!!section.checkboxValues?.[opt]} onChange={e=>handleMcqCheckboxChange(sidx, opt, e.target.checked, true)} /> {opt}
                              </label>
                            ))}
                  </div>
                        )}
                        {Array.isArray(section.items) && section.items.map((item, iidx) => (
                          <div key={iidx} className="mb-2" style={{paddingLeft:8}}>
                            {/* Main title styling */}
                                {item.isMainTitle ? (
                              <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:12, marginTop:8}}>
                                    {item.title}
                                  </div>
                                ) : item.isStepHeader ? (
                                  <div className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8, marginTop:12, paddingLeft:16}}>
                                    {item.title}
                                  </div>
                            ) : item.hasCheckbox && !item.textField?.isTextBox ? (
                              /* For checkbox items, show title with checkbox on the right */
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6}}>
                                <div className="fw-medium" style={{color:'#8b1634'}}>{item.title || item.text}</div>
                                <input type="checkbox" className="form-check-input theme-checkbox" checked={!!item.checkboxValue} onChange={e=>handleMcqItemCheckboxChange(sidx, iidx, e.target.checked, true)} />
                                      </div>
                            ) : (
                              <div className="fw-medium" style={{marginBottom:6, color:'#8b1634'}}>{item.title || item.text}</div>
                            )}
                            <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center'}}>
                              {Array.isArray(item.options) && item.options.map(opt => (
                                <label key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                  <input type="radio" className="form-check-input theme-checkbox" name={`edit-${sidx}-${iidx}`} checked={item.value === opt} onChange={()=>handleMcqChange(sidx, iidx, opt, true)} /> {opt}
                                </label>
                              ))}
                              {item.textField?.isTextBox && (
                                <div style={{display:'flex', alignItems:'center', gap:12}}>
                                  <input 
                                    type="text" 
                                    className="form-control" 
                                    style={{
                                      maxWidth: 320,
                                          fontSize: '16px',
                                      fontFamily: 'system-ui, -apple-system, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
                                      lineHeight: '1.5',
                                          padding: '8px 12px'
                                    }}
                                    placeholder={item.textField?.hintText || ''} 
                                     value={item.textFieldValue !== undefined ? item.textFieldValue : (item.textField?.valueType || item.textField?.value || '')}
                                        onChange={e=>handleMcqTextFieldChange(sidx, iidx, e.target.value, true)} 
                                      />
                                  {/* Only show N/A checkbox if the textField has na property set to true */}
                                  {item.textField?.na && (
                                    <label style={{display:'flex', alignItems:'center', gap:6}}>
                                      <input type="checkbox" className="form-check-input theme-checkbox" checked={!!item.textField?.na} onChange={e=>handleMcqNAChange(sidx, iidx, e.target.checked, true)} /> N/A
                                    </label>
                                    )}
                                </div>
                              )}
                                  {/* Show print date field */}
                              {item.printDate?.isPrintDate && (
                                    <div style={{display:'flex', alignItems:'center', gap:12}}>
                                      <label style={{fontSize:'14px', fontWeight:'500'}}>Date:</label>
                                  <div className="site-register-datepicker-wrap" style={{ maxWidth: 200 }}>
                                    <DatePicker
                                      {...siteRegisterDatePickerProps}
                                      selected={parseSiteRegisterDate(item.printDate?.printValue)}
                                      onChange={(d) => {
                                        const updatedSections = [...editRecord.mcqQuestionsList];
                                        updatedSections[sidx].items[iidx].printDate.printValue = d ? formatSiteRegisterDate(d) : '';
                                        setEditRecord({ ...editRecord, mcqQuestionsList: updatedSections });
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                                  {/* Show signature field - Disabled in Edit */}
                              {item.signature?.isSigned && (
                                    <div style={{display:'flex', flexDirection:'column', gap:8}}>
                                      <label style={{fontSize:'14px', fontWeight:'500'}}>Signature:</label>
                                  <div style={{ ...adminEditSigBoxStyle(), width: '300px', minHeight: 80 }}>
                                    {item.signature?.signValue ? (
                                      <img 
                                        src={item.signature.signValue.startsWith('data:image') ? item.signature.signValue : `data:image/png;base64,${item.signature.signValue}`}
                                        alt="Item Signature" 
                                        style={{
                                          maxWidth: '100%',
                                          maxHeight: '60px',
                                          objectFit: 'contain',
                                          border: '1px solid #ddd',
                                          borderRadius: '8px',
                                          padding: '4px',
                                          backgroundColor: 'white',
                                          opacity: 1,
                                          filter: 'contrast(1.1) brightness(1)'
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <div style={{padding: '20px', color: '#666', fontSize: '14px'}}>
                                        Signature (Disabled in Edit)
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
              <div className="modal-footer" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)} disabled={isSaving}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload Pre-Fabricated Building Form File"
        importPath="pre-fabricated/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRecords(prev => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete Pre-Fabricated Tower Inspection</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">View Pre-Fabricated Tower Inspection</h5>
                <button type="button" className="btn-close" onClick={()=>setShowViewModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-2 col-4">
                    <label><strong>User ID:</strong></label>
                    <p>{viewRecord.userId || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Inspector:</strong></label>
                    <p>{viewRecord.inspector || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Employer No:</strong></label>
                    <p>{viewRecord.employerNo || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>PASMA Card No:</strong></label>
                    <p>{viewRecord.pasmaCardNo || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>User Name 1:</strong></label>
                    <p>{viewRecord.userName1 || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Employer No 1:</strong></label>
                    <p>{viewRecord.employerNo1 || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>PASMA Card No 1:</strong></label>
                    <p>{viewRecord.pasmaCardNo1 || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>User Name 2:</strong></label>
                    <p>{viewRecord.userName2 || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Employer No 2:</strong></label>
                    <p>{viewRecord.employerNo2 || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>PASMA Card No 2:</strong></label>
                    <p>{viewRecord.pasmaCardNo2 || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Inspection Date:</strong></label>
                    <p>{viewRecord.inspectionDate || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Tower No:</strong></label>
                    <p>{viewRecord.towerNo || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Platform Height:</strong></label>
                    <p>{viewRecord.platformHeight || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-4">
                    <label><strong>Project Job No:</strong></label>
                    <p>{viewRecord.projectJobNo || 'N/A'}</p>
                  </div>
                  <div className="mb-2 col-md-6">
                    <label><strong>Reason for Tower:</strong></label>
                    <p>{viewRecord.reasonForTower || 'N/A'}</p>
                  </div>

                  {/* MCQ Sections - View - Updated to match PowAssessmentPage structure */}
                  <div className="mb-2 col-md-6">
                    <label className="fw-bold" style={{color:'#8b1634'}}>MCQ Questions List</label>
                    {Array.isArray(viewRecord.mcqQuestionsList) && viewRecord.mcqQuestionsList.map((section, sidx) => (
                      <div key={sidx} style={{border:'1px solid #eee', borderRadius:8, padding:12, marginTop:8}}>
                        {section.header === 'Steps List' ? (
                          <div>
                            {/* Show main title from API data */}
                            <div className="fw-bold" style={{marginBottom:8, color:'#8b1634', fontSize:'18px'}}>
                              {section.mainTitle || 'Steps List'}
                            </div>
                          </div>
                        ) : (
                        <div className="fw-bold" style={{marginBottom:8}}>{section.header}</div>
                        )}
                        {Array.isArray(section.checkboxOptions) && section.checkboxOptions.length > 0 && (
                          <div className="mb-2" style={{display:'flex', flexWrap:'wrap', gap:16}}>
                            {section.checkboxOptions.map(opt => (
                              <div key={opt} style={{display:'flex', alignItems:'center', gap:6}}>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: section.checkboxValues?.[opt] ? '#d4edda' : '#f8d7da',
                                  color: section.checkboxValues?.[opt] ? '#155724' : '#721c24',
                                  fontWeight: '500'
                                }}>
                                  {opt}: {section.checkboxValues?.[opt] ? '✓' : '✗'}
                                </span>
                              </div>
                            ))}
                  </div>
                        )}
                        {Array.isArray(section.items) && section.items.map((item, iidx) => (
                          <div key={iidx} className="mb-2" style={{paddingLeft:8, borderBottom:'1px solid #f0f0f0', paddingBottom:'8px'}}>
                            {item.isMainTitle ? (
                              <div className="fw-bold" style={{color:'#8b1634', fontSize:'18px', marginBottom:12, marginTop:8}}>
                                {item.title}
                              </div>
                            ) : item.isStepHeader ? (
                              <div className="fw-bold" style={{color:'#8b1634', fontSize:'16px', marginBottom:8, marginTop:12, paddingLeft:16}}>
                                {item.title}
                              </div>
                            ) : (
                              <div className="fw-medium" style={{marginBottom:6, color:'#8b1634'}}>{item.title || item.text}</div>
                            )}
                            <div style={{display:'flex', flexWrap:'wrap', gap:12, alignItems:'center'}}>
                              {Array.isArray(item.options) && item.options.length > 0 && (
                              <div style={{display:'flex', alignItems:'center', gap:6}}>
                                  <strong>Answer:</strong>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  backgroundColor: item.value === 'Yes' ? '#d4edda' : item.value === 'No' ? '#f8d7da' : '#e2e3e5',
                                  color: item.value === 'Yes' ? '#155724' : item.value === 'No' ? '#721c24' : '#383d41',
                                    fontWeight: '500'
                                }}>
                                  {item.value || 'Not answered'}
                                </span>
                              </div>
                              )}
                              {item.hasCheckbox && (
                                <div style={{display:'flex', alignItems:'center', gap:6}}>
                                  <strong>Checkbox:</strong>
                                  <span style={{
                                    padding: '4px 8px', 
                                    borderRadius: '4px', 
                                    backgroundColor: item.checkboxValue ? '#d4edda' : '#f8d7da',
                                    color: item.checkboxValue ? '#155724' : '#721c24',
                                    fontWeight: '500'
                                  }}>
                                    {item.checkboxValue ? '✓ Checked' : '✗ Unchecked'}
                                  </span>
                                </div>
                              )}
                              {item.textField?.isTextBox && (item.textFieldValue || item.textField?.value) && (
                                <div style={{display:'flex', alignItems:'center', gap:6}}>
                                  <strong>Text Input:</strong>
                                  <span style={{padding: '4px 8px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6'}}>
                                    {item.textFieldValue || item.textField.value}
                                  </span>
                                </div>
                              )}
                              {item.printDate?.isPrintDate && (
                                <div style={{display:'flex', alignItems:'center', gap:6}}>
                                  <strong>Date:</strong>
                                  <span style={{
                                    padding: '4px 8px', 
                                    backgroundColor: item.printDate.printValue ? '#d4edda' : '#f8d7da', 
                                    borderRadius: '4px', 
                                    border: '1px solid #dee2e6',
                                    color: item.printDate.printValue ? '#155724' : '#721c24',
                                    fontWeight: '500'
                                  }}>
                                    {item.printDate.printValue || 'Not set'}
                                  </span>
                                </div>
                              )}
                              {item.signature?.isSigned && (
                                <div style={{display:'flex', alignItems:'center', gap:6}}>
                                  <strong>Signature:</strong>
                                  <div style={{display:'flex', alignItems:'center', gap:4}}>
                                    <span style={{
                                      color: item.signature.signValue ? '#28a745' : '#dc3545', 
                                      fontWeight: '500'
                                    }}>
                                      {item.signature.signValue ? '✓ Signed' : '✗ Not signed'}
                                    </span>
                                    {item.signature.signValue && (
                                      <img 
                                        src={item.signature.signValue.startsWith('data:image') ? item.signature.signValue : `data:image/png;base64,${item.signature.signValue}`}
                                        alt="Signature"
                                        style={{width: '80px', height: '40px', border: '1px solid #ccc', borderRadius: '4px'}}
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>

                  {viewRecord.signature && (
                    <div className="mb-2 col-md-6">
                      <label><strong>Main Signature:</strong></label>
                      <div style={{marginTop: '8px'}}>
                        <img 
                          src={viewRecord.signature.startsWith('data:image') ? viewRecord.signature : `data:image/png;base64,${viewRecord.signature}`}
                          alt="Signature"
                          style={{maxWidth: '300px', maxHeight: '150px', border: '1px solid #ccc', borderRadius: '4px'}}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowViewModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
}

export default PreFabricatedPage;
